import {
  currencyFormat,
  currencyFormat4,
  formatPercent,
  formatPercentSm,
  numberFormat,
  tFormat,
  tFormatAnalytics,
  tIntervalsAnalytics,
} from "@/_core/_helpers/_parametric_tools";
import {
  longShort,
  populateTable,
} from "@/_core/charts/D3ChartAnalyticsChartHelpers";
import {
  dateRangeToString,
  formatDate_DDMMMYHMS,
  lengthByDate,
  noNanNum,
} from "@/_core/_helpers/_helpers";
import { sortByKey } from "@/_core/_helpers/_helpers.js";
import { getSpotData } from "@/_core/data/api_calls/getSpotData";
import { saveUserPreferences } from "@/_core/data/SaveToDB";
import {
  format,
  max,
  min,
  nest,
  sum,
  timeDay,
  timeFormat,
  timeMonday,
  timeMonth,
  timeParse,
  timeYear,
  values,
} from "d3";

export function weiAve(p, q, p2, qTot) {
  return (p * q + p2 * (qTot - q)) / qTot;
}

// ===== nest ===== //
// buckets future expo and hedges
export function dataCompressCurrency(
  _this,
  data,
  intervalGroup,
  resultField,
  futureBool,
  isCents = 1,
) {
  let refDate = latestHedgesRefDate(_this.selectedExecutionTime);
  let sign = Math.sign(sum(data, (d) => d.cash_flow));
  sign = sign === 0 ? Math.sign(sum(data, (d) => d.balance)) : sign;
  sign = sign === 0 ? 1 : sign;
  _this[resultField] = nest()
    .key(function(d) {
      return timeCompare(d.date, intervalGroup, data);
    })
    .rollup(function(v) {
      let date = timeCompare(values(v).pop().date, intervalGroup, data);
      let balance,
        undated,
        cash_flow,
        cash_flow_au,
        program,
        budget_expo,
        budgetPRatio;
      // ====================== Qty ======================
      if (_this.isCurrency) {
        balance =
          sign *
          sum(v, function(d) {
            return d.balance;
          });
        budgetPRatio = 1;
        budget_expo =
          sign *
          budgetPRatio *
          sum(v, function(d) {
            return d.budget_expo;
          });
        undated =
          sign *
          sum(v, function(d) {
            return d.undated;
          });
        cash_flow =
          sign *
          sum(v, function(d) {
            return d.cash_flow;
          });
        cash_flow_au = cash_flow;
        program =
          sign *
          sum(v, function(d) {
            return d.program;
          });
      } else {
        const cash_flow_ratio =
          sign *
          sum(v, function(d) {
            return d.cash_flow;
          });
        const balance_ratio =
          sign *
          sum(v, function(d) {
            return d.balance;
          });
        cash_flow_au =
          sign *
          sum(v, function(d) {
            return d.cash_flow * d.ratio * d.unit_change;
          });
        balance =
          sign *
          sum(v, function(d) {
            return d.balance * d.ratio * d.unit_change;
          });
        const bal_cf = cash_flow_ratio / balance_ratio;
        cash_flow = balance * bal_cf;
        const budgetPRatio =
          (sign *
            sum(v, function(d) {
              return d.cash_flow !== 0
                ? d.ratio *
                    d.ratio *
                    d.cash_flow *
                    d.unit_change *
                    d.unit_change
                : 0;
            })) /
          cash_flow;
        budget_expo =
          sign *
          budgetPRatio *
          sum(v, function(d) {
            return d.budget_expo;
          });
        undated =
          sign *
          sum(v, function(d) {
            return d.undated * d.ratio * d.unit_change;
          });

        program =
          sign *
          sum(v, function(d) {
            return d.program * d.ratio * d.unit_change;
          });
      }
      budget_expo = budget_expo == 0 ? NaN : budget_expo;
      const hedge =
        sign *
        sum(v, function(d) {
          return d.hedge;
        });
      const fixed =
        sign *
        sum(v, function(d) {
          return d.fixed;
        });
      const protectedQ = hedge - fixed;
      const last_m_hedge =
        sign *
        sum(v, function(d) {
          return d.trade_date >= refDate ? d.hedge : 0;
        });
      const newHedge =
        sign *
        sum(v, function(d) {
          return d.newHedge;
        });
      const totalHedgePercent =
        (cash_flow !== 0 ? (newHedge + hedge) / cash_flow : 0) * 100;
      const newHedgePercent =
        (cash_flow !== 0 ? newHedge / cash_flow : 0) * 100;
      // ====================== Rates ======================
      let m2m_rate;
      if (cash_flow === 0) {
        m2m_rate = NaN;
      } else {
        m2m_rate =
          sum(v, function(d) {
            return d.fwd * d.cash_flow * d.ratio * d.unit_change;
          }) /
          sum(v, function(d) {
            return d.cash_flow * d.ratio * d.unit_change;
          });
      }
      let budget_rate =
        (sign *
          sum(v, function(d) {
            return d.cash_flow !== 0
              ? d.budget_rate * d.cash_flow * d.ratio * d.unit_change
              : NaN;
          })) /
        cash_flow_au;
      budget_rate =
        Number.isFinite(budget_rate) & (Math.abs(+budget_rate) > 0.00001)
          ? budget_rate
          : m2m_rate;
      const hedge_rate =
        (sign *
          sum(v, function(d) {
            return d.fwd * d.hedge;
          }) +
          (sign *
            sum(v, function(d) {
              return d.hedgeValue;
            })) /
            isCents) /
        hedge;
      let net_rate = _this.isCurrency
        ? weiAve(isNaN(hedge_rate) ? 0 : hedge_rate, hedge, m2m_rate, cash_flow)
        : hedge === 0
        ? m2m_rate
        : hedge > cash_flow
        ? hedge_rate
        : weiAve(
            Number.isNaN(hedge_rate) ? 0 : hedge_rate,
            hedge,
            m2m_rate,
            cash_flow,
          );
      // Mark to market
      const m2m = sum(v, function(d) {
        return d.fwd * d.cash_flow * isCents;
      });
      const hedge_m2m = sum(v, function(d) {
        return d.hedgeValue;
      });
      const net_m2m = m2m + hedge_m2m;
      // delta
      const delta = sum(v, function(d) {
        return d.delta * d.notional;
      });
      // ===== percentages and other differences =====
      // let budgetQPerc =
      //   formatPercentSm(budgetQ / cash_flow);
      const marketPPerc = m2m_rate / budget_rate - 1;
      const fixedPPerc = hedge_rate / budget_rate - 1;
      const netPPerc = net_rate / budget_rate - 1;

      const budgetQPerc = budget_expo / cash_flow;
      const hedgedQPerc = hedge / cash_flow;
      const fixedQPerc = fixed / cash_flow;
      const protectedQPerc = protectedQ / cash_flow;
      const programQPerc = program / cash_flow;
      const ratioQPerc = balance / cash_flow;
      // 1st diff: Net vs Market
      const difference =
        isCents * sign * Math.max(cash_flow, balance) * (net_rate - m2m_rate);
      // 2nd diff: hedge P&L
      const differenceOne = sum(v, function(d) {
        return d.hedgeValue;
      });

      return {
        balance: balance,
        budget_expo: budget_expo,
        budget_rate: budget_rate,
        budgetQPerc: budgetQPerc,
        cash_flow: cash_flow,
        date: date,
        delta: delta,
        difference: difference,
        differenceOne: differenceOne,
        fixedPPerc: fixedPPerc,
        hedgedQPerc: hedgedQPerc,
        fixedQPerc: fixedQPerc,
        protectedQPerc: protectedQPerc,
        programQPerc: programQPerc,
        hedge_m2m: hedge_m2m,
        hedge_rate: hedge_rate,
        hedge: hedge,
        fixed: fixed,
        protected: protectedQ,
        last_m_hedge: last_m_hedge,
        m2m_rate: m2m_rate,
        m2m: m2m,
        marketPPerc: marketPPerc,
        net_m2m: net_m2m,
        net_rate: net_rate,
        netPPerc: netPPerc,
        newHedge: newHedge,
        newHedgePercent: newHedgePercent,
        program: program,
        ratioQPerc: ratioQPerc,
        sign: sign,
        totalHedgePercent: totalHedgePercent,
        undated: undated,
      };
    })
    .entries(data)
    .map(function(d) {
      return d.value;
    });
  _this[resultField].forEach(function(d, i) {
    d.id = i;
  });
  // ================== Functions Calls ==========================
  if (futureBool) {
    _this.xRange = _this[resultField].map((d) => d.date);
    _this.xRange.push(setMax(data, _this.xRange, intervalGroup));
    if (!_this.zoomInitialized) {
      _this.zoomInitial = 0;
      _this.zoomFinal = Math.max(1, _this.xRange.length - 2);
      _this.zoomInitialized = true;
    }
  } else if (intervalGroup === "1M") {
    _this.zoomPastXRange = _this[resultField].map((d) => d.date);
    _this.zoomPastXRange.push(
      setMax(data, _this.zoomPastXRange, intervalGroup),
    );
    if (!_this.zoomPastInitialized) {
      _this.zoomInitialPast = Math.max(0, _this.zoomPastXRange.length - 26);
      _this.zoomFinalPast = Math.max(1, _this.zoomPastXRange.length - 1);
      _this.zoomPastInitialized = true;
    }
  }
}
// totals expo and hedges future
export function dataTotalsCurrency(data, isCents) {
  const len = data.length - 1;
  let date = "...";
  if (len > -1) {
    if (Object.keys(data[0]).indexOf("date") !== -1) {
      date = dateRangeToString(data[0].date, data[len].date, true, "\u2192");
    }
  }
  // totals ===== ===== ===== ===== ===== ===== ===== ===== ===== ===== =====
  const balance = data.map((d) => nonNans(d.balance)).reduce(add, 0);
  const budget_expo = data.map((d) => nonNans(d.budget_expo)).reduce(add, 0);
  const undated = data.map((d) => nonNans(d.undated)).reduce(add, 0);
  const cash_flow = data.map((d) => nonNans(d.cash_flow)).reduce(add, 0);
  const program = data.map((d) => nonNans(d.program)).reduce(add, 0);
  const hedge = data.map((d) => nonNans(d.hedge)).reduce(add, 0);
  const fixed = data.map((d) => nonNans(d.fixed)).reduce(add, 0);
  const protectedQ = data.map((d) => nonNans(d.protected)).reduce(add, 0);
  const difference = data.map((d) => nonNans(d.difference)).reduce(add, 0);
  const differenceOne = data
    .map((d) => nonNans(d.differenceOne))
    .reduce(add, 0);
  const m2m =
    data
      .map(
        (d) =>
          nonNans(d.m2m_rate) * (nonNans(d.balance) + nonNans(d.cash_flow)),
      )
      .reduce(add, 0) * isCents;
  const hedge_m2m = data.map((d) => nonNans(d.hedge_m2m)).reduce(add, 0);
  const last_m_hedge = data.map((d) => nonNans(d.last_m_hedge)).reduce(add, 0);
  const net_m2m = m2m + hedge_m2m;
  const delta = data.map((d) => nonNans(d.delta)).reduce(add, 0);

  // prices ===== ===== ===== ===== ===== ===== ===== ===== ===== ===== =====
  const budget_rate =
    data
      .map((d) => nonNans(d.budget_rate) * nonNans(d.cash_flow))
      .reduce(add, 0) / cash_flow;
  const m2m_rate =
    data.map((d) => nonNans(d.m2m_rate) * nonNans(d.cash_flow)).reduce(add, 0) /
    cash_flow;
  const hedge_rate =
    data.map((d) => nonNans(d.hedge_rate) * nonNans(d.hedge)).reduce(add, 0) /
    hedge;
  const net_rate =
    data
      .map(
        (d) =>
          nonNans(d.net_rate) *
          Math.max(
            nonNans(d.cash_flow),
            nonNans(d.balance),
            nonNans(d.cash_flow),
          ),
      )
      .reduce(add, 0) /
    data
      .map((d) =>
        Math.max(
          nonNans(d.cash_flow),
          nonNans(d.balance),
          nonNans(d.cash_flow),
        ),
      )
      .reduce(add, 0);
  const newHedge = data.map((d) => nonNans(d.newHedge)).reduce(add, 0);
  const newHedgePercent = data
    .map((d) => nonNans(d.newHedgePercent))
    .reduce(add, 0);
  const sign = data.map((d) => nonNans(d.sign)).reduce(add, 0);
  const totalHedgePercent = data
    .map((d) => nonNans(d.totalHedgePercent))
    .reduce(add, 0);

  const marketPPerc = m2m_rate / budget_rate - 1;
  const fixedPPerc = hedge_rate / budget_rate - 1;
  const netPPerc = net_rate / budget_rate - 1;
  const budgetQPerc = budget_expo / cash_flow - 1;
  const fixedQPerc = fixed / cash_flow;
  const protectedQPerc = protectedQ / cash_flow;
  const hedgedQPerc = hedge / cash_flow;
  const programQPerc = program / cash_flow;
  const ratioQPerc = balance / cash_flow;

  // derivatives
  function nonNanAve(data, field) {
    return data
      .map((d) => nonNans(d[field]))
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  }
  function weaAve(data, field, weiField) {
    return data
      .map((d) => nonNans(d[field]) * nonNans(d[weiField]))
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  }
  const notionalLFwd = nonNanAve(data, "notionalLFwd");
  const notionalSFwd = nonNanAve(data, "notionalSFwd");
  const notionalLCall = nonNanAve(data, "notionalLCall");
  const notionalSCall = nonNanAve(data, "notionalSCall");
  const notionalLPut = nonNanAve(data, "notionalLPut");
  const notionalSPut = nonNanAve(data, "notionalSPut");

  const lFwd = weaAve(data, "lFwd", "notionalLFwd") / notionalLFwd;
  const sFwd = weaAve(data, "sFwd", "notionalSFwd") / notionalSFwd;
  const lCall = weaAve(data, "lCall", "notionalLCall") / notionalLCall;
  const sCall = weaAve(data, "sCall", "notionalSCall") / notionalSCall;
  const lPut = weaAve(data, "lPut", "notionalLPut") / notionalLPut;
  const sPut = weaAve(data, "sPut", "notionalSPut") / notionalSPut;
  // const lCallKO = weaAve(data, "lCallKO", "call", "ko");
  // const sCallKO = weaAve(data, "sCallKO", "call", "ko");
  // const lPutKO = weaAve(data, "lPutKO", "put", "ko");
  // const sPutKO = weaAve(data, "sPutKO", "put", "ko");
  // const lCKo = currencyFormat4(averageStrike(data, "LONG", "call", "koB"));
  // const sCKo = currencyFormat4(averageStrike(data, "SHORT", "call", "koB"));
  // const lPKo = currencyFormat4(averageStrike(data, "LONG", "put", "koB"));
  // const sPKo = currencyFormat4(averageStrike(data, "SHORT", "put", "koB"));

  return {
    balance: balance,
    budget_expo: budget_expo,
    budget_rate: budget_rate,
    budgetQPerc: budgetQPerc,
    cash_flow: cash_flow,
    date: date,
    delta: delta,
    difference: difference,
    differenceOne: differenceOne,
    fixedPPerc: fixedPPerc,
    fixedQPerc: fixedQPerc,
    hedgedQPerc: hedgedQPerc,
    protectedQPerc: protectedQPerc,
    programQPerc: programQPerc,
    hedge_m2m: hedge_m2m,
    hedge_rate: hedge_rate,
    hedge: hedge,
    fixed: fixed,
    protected: protectedQ,
    last_m_hedge: last_m_hedge,
    m2m_rate: m2m_rate,
    m2m: m2m,
    marketPPerc: marketPPerc,
    net_m2m: net_m2m,
    net_rate: net_rate,
    netPPerc: netPPerc,
    newHedge: newHedge,
    newHedgePercent: newHedgePercent,
    program: program,
    ratioQPerc: ratioQPerc,
    sign: sign,
    totalHedgePercent: totalHedgePercent,
    undated: undated,
    // derivatives
    lFwd: lFwd,
    sFwd: sFwd,
    lCall: lCall,
    sCall: sCall,
    lPut: lPut,
    sPut: sPut,
    // lCallKO: lCallKO,
    // lCKo: lCKo,
    // sCallKO: sCallKO,
    // sCKo: sCKo,
    // lPutKO: lPutKO,
    // lPKo: lPKo,
    // sPutKO: sPutKO,
    // sPKo: sPKo,
    notionalLFwd: notionalLFwd,
    notionalSFwd: notionalSFwd,
    notionalLCall: notionalLCall,
    notionalSCall: notionalSCall,
    notionalLPut: notionalLPut,
    notionalSPut: notionalSPut,
  };
}
// buckets simulations
export function dataCompressCurrencySims(
  _this,
  data,
  intervalGroup,
  resultField,
  compareField,
  isCents = 1,
) {
  let sign = Math.sign(sum(_this.chartsDataRaw, (d) => d.cash_flow));
  sign =
    sign === 0 ? Math.sign(sum(_this.chartsDataRaw, (d) => d.balance)) : sign;
  sign = sign === 0 ? 1 : sign;
  if (data.length > 0) {
    let data2 = simulationData(_this, data);
    // data = data2.concat(data);
    _this[resultField] = nest()
      .key(function(d) {
        return timeCompare(d.date, intervalGroup, data2);
      })
      .rollup(function(v) {
        let date = timeCompare(values(v).pop().date, intervalGroup, data2);
        let hedge =
          sign *
          sum(v, function(d) {
            return d.hedge;
          });
        let cash_flow;
        if (_this.isCurrency) {
          cash_flow =
            sign *
            sum(v, function(d) {
              return d.cash_flow;
            });
        } else {
          let cash_flow_ratio =
            sign *
            sum(v, function(d) {
              return d.cash_flow;
            });
          let balance_ratio =
            sign *
            sum(v, function(d) {
              return d.balance;
            });
          cash_flow =
            sign *
            sum(v, function(d) {
              return d.cash_flow * d.ratio * d.unit_change;
            });
          let balance =
            sign *
            sum(v, function(d) {
              return d.balance * d.ratio * d.unit_change;
            });
          let bal_cf = 1 - balance_ratio / cash_flow_ratio;
          cash_flow = balance + bal_cf * cash_flow;
        }
        // Rates
        let cf = sum(v, function(d) {
          return d.cash_flow * d.ratio * d.unit_change;
        });
        let m2m_rate_up =
          cash_flow === 0
            ? NaN
            : sum(v, function(d) {
                return d.percentiles_up * d.cash_flow * d.ratio * d.unit_change;
              }) / cf;
        let m2m_rate_dn =
          cash_flow === 0
            ? NaN
            : sum(v, function(d) {
                return d.percentiles_dn * d.cash_flow * d.ratio * d.unit_change;
              }) / cf;
        let hedge_rate_up =
          (sign *
            sum(v, function(d) {
              return d.percentiles_up * d.hedge;
            }) +
            (sign *
              sum(v, function(d) {
                return d.hedge_value_up;
              })) /
              isCents) /
          hedge;
        let hedge_rate_dn =
          (sign *
            sum(v, function(d) {
              return d.percentiles_dn * d.hedge;
            }) +
            (sign *
              sum(v, function(d) {
                return d.hedge_value_dn;
              })) /
              isCents) /
          hedge;
        let net_rate_up =
          hedge === 0
            ? m2m_rate_up
            : hedge >= cash_flow
            ? hedge_rate_up
            : weiAve(hedge_rate_up, hedge, m2m_rate_up, cash_flow);
        let net_rate_dn =
          hedge === 0
            ? m2m_rate_dn
            : hedge >= cash_flow
            ? hedge_rate_dn
            : weiAve(hedge_rate_dn, hedge, m2m_rate_dn, cash_flow);
        // Mark to market
        let m2m_up = sum(v, function(d) {
          return d.percentiles_up * d.cash_flow * isCents;
        });
        let m2m_dn = sum(v, function(d) {
          return d.percentiles_dn * d.cash_flow * isCents;
        });
        let hedge_m2m_up = sum(v, function(d) {
          return d.hedge_value_up;
        });
        let hedge_m2m_dn = sum(v, function(d) {
          return d.hedge_value_dn;
        });
        let net_m2m_up = m2m_up + hedge_m2m_up;
        let net_m2m_dn = m2m_dn + hedge_m2m_dn;

        return {
          date: date,
          hedge_m2m_dn: hedge_m2m_dn,
          hedge_m2m_up: hedge_m2m_up,
          hedge_rate_dn: hedge_rate_dn,
          hedge_rate_up: hedge_rate_up,
          hedge: hedge,
          m2m_dn: m2m_dn,
          m2m_rate_dn: m2m_rate_dn,
          m2m_rate_up: m2m_rate_up,
          m2m_up: m2m_up,
          net_m2m_dn: net_m2m_dn,
          net_m2m_up: net_m2m_up,
          net_rate_dn: net_rate_dn,
          net_rate_up: net_rate_up,
        };
      })
      .entries(data2)
      .map(function(d) {
        return d.value;
      });

    if (
      _this[resultField][0].date.getMonth() ===
      _this[compareField][0].date.getMonth()
    ) {
      _this[resultField][0].date = _this[compareField][0].date;
    }
    let dat = _this[compareField].map((d) => d.date).map(Number);
    let idx;
    _this[resultField].forEach(function(d) {
      idx = dat.indexOf(+d.date);
      if (idx > -1) {
        d.id = _this[compareField][idx].id;
      }
    });
  }
}

export function isDerivativePosition(d, position, derivative, dType = null) {
  if (derivative === "forward") {
    return (
      longShort(d.buy_sell) === position &&
      (d.derivative === "forward" || d.derivative === "future")
    );
  } else {
    if (dType === "nonBarrier") {
      return (
        longShort(d.buy_sell) === position &&
        d.derivative === derivative &&
        (d.derivative_type === "EUROPEAN" ||
          d.derivative_type === "AMERICAN" ||
          d.derivative_type === "ASIAN")
      );
    } else if (dType === "ko" || dType === "koB") {
      return (
        longShort(d.buy_sell) === position &&
        d.derivative === derivative &&
        d.derivative_type !== "EUROPEAN" &&
        d.derivative_type !== "AMERICAN" &&
        d.derivative_type !== "ASIAN"
      );
    } else {
      return (
        longShort(d.buy_sell) === position &&
        d.derivative === derivative &&
        d.derivative_type === dType
      );
    }
  }
}

// buckets derivatives
export function averageStrike(data, position, derivative, type = null) {
  let fld = type === "koB" ? "barrier" : "strike";
  return (
    sum(data, function(d) {
      return isDerivativePosition(d, position, derivative, type)
        ? d[fld] * d.notional
        : 0;
    }) /
    sum(data, function(d) {
      return isDerivativePosition(d, position, derivative, type)
        ? d.notional
        : 0;
    })
  );
}
export function averageDelta(data, position, derivative) {
  let notional = sum(data, function(d) {
    return longShort(d.buy_sell) === position && d.derivative === derivative
      ? d.notional
      : 0;
  });
  let delta =
    sum(data, function(d) {
      return longShort(d.buy_sell) === position && d.derivative === derivative
        ? d.delta * d.notional
        : 0;
    }) / notional;
  return [delta, notional];
}
export function allDerivativesAveAndDeltas(data, date) {
  let lFwd = averageStrike(data, "LONG", "forward");
  let sFwd = averageStrike(data, "SHORT", "forward");
  let lCall = averageStrike(data, "LONG", "call", "nonBarrier");
  let sCall = averageStrike(data, "SHORT", "call", "nonBarrier");
  let lPut = averageStrike(data, "LONG", "put", "nonBarrier");
  let sPut = averageStrike(data, "SHORT", "put", "nonBarrier");
  let lCallKO = averageStrike(data, "LONG", "call", "ko");
  let sCallKO = averageStrike(data, "SHORT", "call", "ko");
  let lPutKO = averageStrike(data, "LONG", "put", "ko");
  let sPutKO = averageStrike(data, "SHORT", "put", "ko");
  let lCKo = currencyFormat4(averageStrike(data, "LONG", "call", "koB"));
  let sCKo = currencyFormat4(averageStrike(data, "SHORT", "call", "koB"));
  let lPKo = currencyFormat4(averageStrike(data, "LONG", "put", "koB"));
  let sPKo = currencyFormat4(averageStrike(data, "SHORT", "put", "koB"));
  let lFwdDelta = averageDelta(data, "LONG", "forward");
  let sFwdDelta = averageDelta(data, "SHORT", "forward");
  let lCallDelta = averageDelta(data, "LONG", "call");
  let sCallDelta = averageDelta(data, "SHORT", "call");
  let lPutDelta = averageDelta(data, "LONG", "put");
  let sPutDelta = averageDelta(data, "SHORT", "put");
  return {
    date: date,
    lFwd: lFwd,
    sFwd: sFwd,
    lCall: lCall,
    sCall: sCall,
    lPut: lPut,
    sPut: sPut,
    lCallKO: lCallKO,
    lCKo: lCKo,
    sCallKO: sCallKO,
    sCKo: sCKo,
    lPutKO: lPutKO,
    lPKo: lPKo,
    sPutKO: sPutKO,
    sPKo: sPKo,
    lFwdDelta: lFwdDelta[0],
    sFwdDelta: sFwdDelta[0],
    lCallDelta: lCallDelta[0],
    sCallDelta: sCallDelta[0],
    lPutDelta: lPutDelta[0],
    sPutDelta: sPutDelta[0],
    notionalLFwd: lFwdDelta[1],
    notionalSFwd: sFwdDelta[1],
    notionalLCall: lCallDelta[1],
    notionalSCall: sCallDelta[1],
    notionalLPut: lPutDelta[1],
    notionalSPut: sPutDelta[1],
  };
}
export function nestDerivativeAverages(data, intervalGroup, _this) {
  let dateFld = _this.hedgeDateField;
  return nest()
    .key(function(d) {
      return timeCompare(d[dateFld], intervalGroup, data);
    })
    .rollup(function(v) {
      let date = timeCompare(values(v).pop()[dateFld], intervalGroup, data);
      return allDerivativesAveAndDeltas(v, date);
    })
    .entries(data)
    .map(function(d) {
      return d.value;
    });
}
// buckets derivatives past
export function pastDataCompressAveragesCurrency(
  _this,
  data,
  intervalGroup,
  resultField,
  endResultField,
) {
  _this[resultField] = nestDerivativeAverages(data, intervalGroup, _this);
  if (_this[resultField].length > 0) {
    _this[resultField][0].date = timeMonth(_this[resultField][0].date);
  }
  _this[resultField].forEach(function(d) {
    d.id = -1;
  });
  let dat = _this[resultField].map((d) => d.date).map(Number);
  let idx;
  _this[endResultField].forEach(function(d, i) {
    idx = dat.indexOf(+d.date);
    if (idx > -1) {
      _this[resultField][idx].id = i;
      d.lFwd = _this[resultField][idx].lFwd;
      d.sFwd = _this[resultField][idx].sFwd;
      d.lCall = _this[resultField][idx].lCall;
      d.sCall = _this[resultField][idx].sCall;
      d.lPut = _this[resultField][idx].lPut;
      d.sPut = _this[resultField][idx].sPut;
      d.lFwdDelta = 0;
      d.sFwdDelta = 0;
      d.lCallDelta = 0;
      d.sCallDelta = 0;
      d.lPutDelta = 0;
      d.sPutDelta = 0;
      d.notionalLFwd = _this[resultField][idx].notionalLFwd;
      d.notionalSFwd = _this[resultField][idx].notionalSFwd;
      d.notionalLCall = _this[resultField][idx].notionalLCall;
      d.notionalSCall = _this[resultField][idx].notionalSCall;
      d.notionalLPut = _this[resultField][idx].notionalLPut;
      d.notionalSPut = _this[resultField][idx].notionalSPut;
    } else {
      d.lFwd = NaN;
      d.sFwd = NaN;
      d.lCall = NaN;
      d.sCall = NaN;
      d.lPut = NaN;
      d.sPut = NaN;
      d.lFwdDelta = NaN;
      d.sFwdDelta = NaN;
      d.lCallDelta = NaN;
      d.sCallDelta = NaN;
      d.lPutDelta = NaN;
      d.sPutDelta = NaN;
      d.notionalLFwd = NaN;
      d.notionalSFwd = NaN;
      d.notionalLCall = NaN;
      d.notionalSCall = NaN;
      d.notionalLPut = NaN;
      d.notionalSPut = NaN;
    }
  });
}

// buckets derivatives future
export function dataCompressAveragesCurrency(
  _this,
  data,
  intervalGroup,
  resultField,
  endResultField,
  // initialDate = null
) {
  let dateList;
  dateList = _this.processData.createWeekByWeekDays(
    _this.initialDate,
    _this.finalDate,
  );
  let dataRaw = [];
  dateList.forEach((d) =>
    dataRaw.push({
      date: d,
      buy_sell: "long",
      derivative: "forward",
      strike: 0,
      notional: 0,
    }),
  );
  data = data.concat(dataRaw);
  _this[resultField] = nestDerivativeAverages(data, intervalGroup, _this);
  _this[resultField].forEach(function(d, i) {
    d.id = i;
  });
  let dat = _this[resultField].map((d) => d.date).map(Number);
  let idx;
  _this[endResultField].forEach(function(d, i) {
    idx = dat.indexOf(+d.date);
    if (idx > -1) {
      _this[resultField][idx].id = i;
      d.lFwd = _this[resultField][idx].lFwd;
      d.sFwd = _this[resultField][idx].sFwd;
      d.lCall = _this[resultField][idx].lCall;
      d.lCallKO = _this[resultField][idx].lCallKO;
      d.lCKo = _this[resultField][idx].lCKo;
      d.sCall = _this[resultField][idx].sCall;
      d.sCallKO = _this[resultField][idx].sCallKO;
      d.sCKo = _this[resultField][idx].sCKo;
      d.lPut = _this[resultField][idx].lPut;
      d.lPutKO = _this[resultField][idx].lPutKO;
      d.lPKo = _this[resultField][idx].lPKo;
      d.sPut = _this[resultField][idx].sPut;
      d.sPutKO = _this[resultField][idx].sPutKO;
      d.sPKo = _this[resultField][idx].sPKo;
      d.lFwdDelta = _this[resultField][idx].lFwdDelta;
      d.sFwdDelta = _this[resultField][idx].sFwdDelta;
      d.lCallDelta = _this[resultField][idx].lCallDelta;
      d.sCallDelta = _this[resultField][idx].sCallDelta;
      d.lPutDelta = _this[resultField][idx].lPutDelta;
      d.sPutDelta = _this[resultField][idx].sPutDelta;
      d.notionalLFwd = _this[resultField][idx].notionalLFwd;
      d.notionalSFwd = _this[resultField][idx].notionalSFwd;
      d.notionalLCall = _this[resultField][idx].notionalLCall;
      d.notionalSCall = _this[resultField][idx].notionalSCall;
      d.notionalLPut = _this[resultField][idx].notionalLPut;
      d.notionalSPut = _this[resultField][idx].notionalSPut;
    } else {
      d.lFwd = NaN;
      d.sFwd = NaN;
      d.lCall = NaN;
      d.sCall = NaN;
      d.lPut = NaN;
      d.sPut = NaN;
      d.lFwdDelta = NaN;
      d.sFwdDelta = NaN;
      d.lCallDelta = NaN;
      d.sCallDelta = NaN;
      d.lPutDelta = NaN;
      d.sPutDelta = NaN;
      d.notionalLFwd = NaN;
      d.notionalSFwd = NaN;
      d.notionalLCall = NaN;
      d.notionalSCall = NaN;
      d.notionalLPut = NaN;
      d.notionalSPut = NaN;
    }
  });
}
// totals derivatives future
export function dataTotalsDerivativesCurrency(data) {
  let averages;
  let date = "ALL DATES";
  averages = allDerivativesAveAndDeltas(data, date);
  return averages;
}
// totals simulations
export function dataTotalsCurrencySims(data, isCents) {
  let averages;
  // Rates
  let hedge = data.map((d) => nonNans(d.hedge)).reduce(add, 0);
  let cash_flow = data.map((d) => nonNans(d.cash_flow)).reduce(add, 0);
  let m2m_rate_dn =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_dn) *
          (nonNans(d.balance) + nonNans(d.cash_flow)),
      )
      .reduce(add, 0) / cash_flow;
  let m2m_rate_up =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_up) *
          (nonNans(d.balance) + nonNans(d.cash_flow)),
      )
      .reduce(add, 0) / cash_flow;
  let hedge_rate_dn =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_dn) * nonNans(d.hedge) +
          nonNans(d.hedge_value_dn),
      )
      .reduce(add, 0) / hedge;
  let hedge_rate_up =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_up) * nonNans(d.hedge) +
          nonNans(d.hedge_value_up),
      )
      .reduce(add, 0) / hedge;
  let net_rate_dn =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_dn) *
            (nonNans(d.balance) + nonNans(d.cash_flow)) +
          nonNans(d.hedge_value_dn),
      )
      .reduce(add, 0) / cash_flow;
  let net_rate_up =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_up) *
            (nonNans(d.balance) + nonNans(d.cash_flow)) +
          nonNans(d.hedge_value_up),
      )
      .reduce(add, 0) / cash_flow;
  // Mark to market
  let m2m_dn =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_dn) *
          (nonNans(d.balance) + nonNans(d.cash_flow)),
      )
      .reduce(add, 0) * isCents;
  let m2m_up =
    data
      .map(
        (d) =>
          nonNans(d.percentiles_up) *
          (nonNans(d.balance) + nonNans(d.cash_flow)),
      )
      .reduce(add, 0) * isCents;
  let hedge_m2m_dn =
    data.map((d) => nonNans(d.hedge_value_dn)).reduce(add, 0) * isCents;
  let hedge_m2m_up =
    data.map((d) => nonNans(d.hedge_value_up)).reduce(add, 0) * isCents;
  let net_m2m_up = m2m_up + hedge_m2m_up;
  let net_m2m_dn = m2m_dn + hedge_m2m_dn;
  averages = {
    m2m_rate_dn: m2m_rate_dn,
    m2m_rate_up: m2m_rate_up,
    hedge_rate_dn: hedge_rate_dn,
    hedge_rate_up: hedge_rate_up,
    net_rate_dn: net_rate_dn,
    net_rate_up: net_rate_up,
    m2m_dn: m2m_dn,
    m2m_up: m2m_up,
    hedge_m2m_dn: hedge_m2m_dn,
    hedge_m2m_up: hedge_m2m_up,
    net_m2m_up: net_m2m_up,
    net_m2m_dn: net_m2m_dn,
  };
  return averages;
}
// ===== topography y-axis range ===== //
export function minMaxYAxisRatesCalc(
  _this,
  minMaxField,
  dataField,
  hedgeField,
  slicedHedgeField,
  slicedSimsField,
) {
  let minMaxRes = { ..._this[minMaxField] };
  if (_this.showBudget) {
    minMaxRes = _this.processData.minMax(
      _this[dataField],
      "budget_rate",
      "budget_rate",
      minMaxRes,
    );
  }
  if (_this.showExpo) {
    minMaxRes = _this.processData.minMax(
      _this[dataField],
      "m2m_rate",
      "m2m_rate",
      minMaxRes,
    );
    if (_this.showSimulations) {
      minMaxRes = _this.processData.minMax(
        _this[slicedSimsField],
        "m2m_rate_up",
        "m2m_rate_dn",
        minMaxRes,
      );
    }
  }
  if (_this.showHedge) {
    minMaxRes = _this.processData.minMax(
      _this[dataField],
      "hedge_rate",
      "hedge_rate",
      minMaxRes,
    );
    if (_this.showSimulations) {
      minMaxRes = _this.processData.minMax(
        _this[slicedSimsField],
        "hedge_rate_up",
        "hedge_rate_dn",
        minMaxRes,
      );
    }
  }
  if (_this.showNet) {
    minMaxRes = _this.processData.minMax(
      _this[dataField],
      "net_rate",
      "net_rate",
      minMaxRes,
    );
    if (_this.showSimulations) {
      minMaxRes = _this.processData.minMax(
        _this[slicedSimsField],
        "net_rate_up",
        "net_rate_dn",
        minMaxRes,
      );
    }
  }
  // if (_this.showProgram) {
  //   minMaxRes[0] = Math.min(
  //     minMaxRes[0],
  //     noNanNum(_this.budgetRate, Infinity),
  //   );
  //   minMaxRes[1] = Math.max(
  //     minMaxRes[1],
  //     noNanNum(_this.budgetRate, -Infinity),
  //   );
  // }
  // let initDate = _this.xRange[_this.zoomInitial];
  // let lastDate = setMax(
  //   _this[dataField][_this[dataField].length - 1],
  //   _this.xRange,
  //   _this.pickedIntervalHistory
  // );
  // _this.xRange[_this.zoomFinal + 1];
  // _this[slicedHedgeField] = _this[hedgeField].filter(
  //   d => d.date >= initDate && d.date < lastDate
  // );

  if (_this.showLFwd) {
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "LONG" && d.derivative === "forward",
      ),
      "strike",
      "strike",
      minMaxRes,
    );
  }
  if (_this.showSFwd) {
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "SHORT" && d.derivative === "forward",
      ),
      "strike",
      "strike",
      minMaxRes,
    );
  }
  if (_this.showLCall) {
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "LONG" && d.derivative === "call",
      ),
      "strike",
      "strike",
      minMaxRes,
    );
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "LONG", "call")),
      "barrier",
      "barrier",
      minMaxRes,
    );
  }
  if (_this.showSCall) {
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "SHORT" && d.derivative === "call",
      ),
      "strike",
      "strike",
      minMaxRes,
    );
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "SHORT", "call")),
      "barrier",
      "barrier",
      minMaxRes,
    );
  }
  if (_this.showLPut) {
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "LONG" && d.derivative === "put",
      ),
      "strike",
      "strike",
      minMaxRes,
    );
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "LONG", "put")),
      "barrier",
      "barrier",
      minMaxRes,
    );
  }
  if (_this.showSPut) {
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "SHORT" && d.derivative === "put",
      ),
      "strike",
      "strike",
      minMaxRes,
    );
    minMaxRes = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "SHORT", "put")),
      "barrier",
      "barrier",
      minMaxRes,
    );
  }
  let del = 0.1 * (minMaxRes[1] - minMaxRes[0]);
  let nonNeg =
    minMaxRes[0] > 0 ? Math.max(0, minMaxRes[0] - del) : minMaxRes[0] - del;
  Object.assign(_this, {
    [minMaxField]: [nonNeg, minMaxRes[1] + del],
  });
}
export function minMaxYAxisRatesPastCalc(
  _this,
  minMaxField,
  dataField,
  hedgeField,
  ohlcField,
  slicedHedgeField,
) {
  if (_this.showBudget) {
    _this[minMaxField] = _this.processData.minMax(
      _this[dataField],
      "budget_rate",
      "budget_rate",
      _this[minMaxField],
    );
  }
  if (_this.showExpo) {
    _this[minMaxField] = _this.processData.minMax(
      _this[dataField],
      "m2m_rate",
      "m2m_rate",
      _this[minMaxField],
    );
  }
  if (_this.showHedge) {
    _this[minMaxField] = _this.processData.minMax(
      _this[dataField],
      "hedge_rate",
      "hedge_rate",
      _this[minMaxField],
    );
  }
  if (_this.showNet) {
    _this[minMaxField] = _this.processData.minMax(
      _this[dataField],
      "net_rate",
      "net_rate",
      _this[minMaxField],
    );
  }
  if (_this.showProgram) {
    _this[minMaxField][0] = Math.min(_this[minMaxField][0], _this.budgetRate);
    _this[minMaxField][1] = Math.max(_this[minMaxField][1], _this.budgetRate);
  }
  let initDate = _this.zoomPastXRange[_this.zoomInitialPast];
  let lastDate = setMax(
    _this[dataField][_this[dataField].length - 1],
    _this.zoomPastXRange,
    _this.pickedIntervalHistory,
  );
  //_this.zoomPastXRange[_this.zoomFinalPast + 1];
  _this[slicedHedgeField] = _this[hedgeField].filter(
    (d) => d.date >= initDate && d.date < lastDate,
  );

  if (_this.showLFwd) {
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "LONG" && d.derivative === "forward",
      ),
      "strike",
      "strike",
      _this[minMaxField],
    );
  }
  if (_this.showSFwd) {
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "SHORT" && d.derivative === "forward",
      ),
      "strike",
      "strike",
      _this[minMaxField],
    );
  }
  if (_this.showLCall) {
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "LONG" && d.derivative === "call",
      ),
      "strike",
      "strike",
      _this[minMaxField],
    );
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "LONG", "call")),
      "barrier",
      "barrier",
      _this[minMaxField],
    );
  }
  if (_this.showSCall) {
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "SHORT" && d.derivative === "call",
      ),
      "strike",
      "strike",
      _this[minMaxField],
    );
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "SHORT", "call")),
      "barrier",
      "barrier",
      _this[minMaxField],
    );
  }
  if (_this.showLPut) {
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "LONG" && d.derivative === "put",
      ),
      "strike",
      "strike",
      _this[minMaxField],
    );
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "LONG", "put")),
      "barrier",
      "barrier",
      _this[minMaxField],
    );
  }
  if (_this.showSPut) {
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter(
        (d) => longShort(d.buy_sell) === "SHORT" && d.derivative === "put",
      ),
      "strike",
      "strike",
      _this[minMaxField],
    );
    _this[minMaxField] = _this.processData.minMax(
      _this[slicedHedgeField].filter((d) => isBarrier(d, "SHORT", "put")),
      "barrier",
      "barrier",
      _this[minMaxField],
    );
  }
  _this[minMaxField] = _this.processData.minMax(
    _this[ohlcField],
    "high",
    "low",
    _this[minMaxField],
  );
  let del = 0.1 * (_this[minMaxField][1] - _this[minMaxField][0]);
  _this[minMaxField] = [
    _this[minMaxField][0] - del,
    _this[minMaxField][1] + del,
  ];
}
export function isBarrier(d, pos = "LONG", der = "call") {
  return (
    longShort(d.buy_sell) === pos &&
    d.derivative === der &&
    (d.derivative_type.indexOf("OUT") > -1 ||
      d.derivative_type.indexOf("IN") > -1)
  );
}
// ===== program ===== //
export function programTotal(program, expoData) {
  let programTot = 0;
  let expo;
  let today;
  let lastDate;
  program.forEach(function(d) {
    if (typeof d.flow != "object") {
      d.flow = JSON.parse(d.flow);
    }
    if (d.objective.toUpperCase() === "BALANCE") {
      expo = calculateBalanceExpo(expoData, d.fk_idportfolio);
      if (d.parameters.toUpperCase() === "MATCH") {
        programTot += (expo * d.flow.values.percentage) / 100;
      } else if (d.parameters.toUpperCase() === "CONSTANT") {
        programTot += (expo * d.flow.values.percentage) / 100;
      } else if (d.parameters.toUpperCase() === "MONTHLY") {
        let months = d.flow.values;
        let percent = 0;
        for (const i in months) {
          percent += months[i];
        }
        programTot += (expo * percent) / 100;
      } else if (d.parameters.toUpperCase() === "QUARTERLY") {
        let quarters = d.flow.values;
        let percent = 0;
        for (const i in quarters) {
          percent += quarters[i];
        }
        programTot += (expo * percent) / 100;
      }
    } else {
      if (d.parameters.toUpperCase() === "CONSTANT") {
        today = new Date();
        lastDate = new Date(
          today.getFullYear(),
          today.getMonth() + d.flow.values.months + 1,
          0,
        );
        expo = calculateCashFlowExpo(
          expoData,
          d.fk_idportfolio,
          today,
          lastDate,
        );
        programTot += (expo * d.flow.values.percentage) / 100;
      } else if (d.parameters.toUpperCase() === "MONTHLY") {
        today = new Date();
        lastDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        let months = d.flow.values;
        for (const i in months) {
          programTot +=
            (months[i] / 100) *
            calculateCashFlowExpo(expoData, d.fk_idportfolio, today, lastDate);
          today = lastDate;
          lastDate = new Date(
            lastDate.getFullYear(),
            lastDate.getMonth() + 2,
            0,
          );
        }
      } else if (d.parameters.toUpperCase() === "QUARTERLY") {
        today = new Date();
        lastDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);
        let quarters = d.flow.values;
        for (const i in quarters) {
          programTot +=
            (quarters[i] / 100) *
            calculateCashFlowExpo(expoData, d.fk_idportfolio, today, lastDate);
          today = lastDate;
          lastDate = new Date(
            lastDate.getFullYear(),
            lastDate.getMonth() + 4,
            0,
          );
        }
      } else if (d.parameters.toUpperCase() === "BUDGET") {
        today = new Date();
        lastDate = new Date(
          today.getFullYear(),
          d.flow.values.percentagemonths - 1,
          0,
        );
        let monthDist =
          12 * (lastDate.getFullYear() - today.getFullYear()) +
          (lastDate.getMonth() - today.getMonth());
        while (monthDist < 3) {
          lastDate = new Date(
            lastDate.getFullYear(),
            lastDate.getMonth() + 13,
            0,
          );
          monthDist =
            12 * (lastDate.getFullYear() - today.getFullYear()) +
            (lastDate.getMonth() - today.getMonth());
        }
        expo = calculateCashFlowExpo(
          expoData,
          d.fk_idportfolio,
          today,
          lastDate,
        );
        programTot += (expo * d.flow.values.percentage) / 100;
      }
    }
  });

  return programTot;
}
export function defaultProgram(_this, asset = "currencies") {
  _this.programData = [
    {
      currency:
        asset === "currencies" ? _this.currencyString : _this.commodityString,
      fk_idportfolio: _this.selectedPortfolios[0].name,
      flow: {
        values: {
          "0": 0,
        },
      },
      objective: "cash_flow",
      parameters: "monthly",
    },
  ];
}
// ===== totals for portfolio chart ===== //
export function calculateExpoHedges(_this) {
  function addByField(data, field, filterField) {
    return data
      .map((d) => (d.derivative_purpose === filterField ? d[field] : 0))
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  }
  function riskProfile(num) {
    if (num < 1.5) {
      return "VERY CONSERVATIVE";
    } else if (num < 2.5) {
      return "CONSERVATIVE";
    } else if (num < 3.5) {
      return "BALANCED";
    } else if (num < 4.5) {
      return "AGGRESSIVE";
    } else {
      return "VERY AGGRESSIVE";
    }
  }
  let expoCF = _this.expoDataRaw
    .map((d) => d.cash_flow)
    .reduce(function(a, b) {
      return a + b;
    }, 0);
  let projected = _this.expoDataRaw
    .map((d) => d.cash_flow)
    .reduce(function(a, b) {
      return a + b;
    }, 0);
  let expoBalance = _this.expoDataRaw
    .map((d) => d.balance + d.undated)
    .reduce(function(a, b) {
      return a + b;
    }, 0);
  let undatedBalance = _this.expoDataRaw
    .map((d) => d.undated)
    .reduce(function(a, b) {
      return a + b;
    }, 0);

  let hedge =
    addByField(_this.hedgeDataRaw, "hedgeNotional", "hedge") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "limited_hedge") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "hedge_and_reduce_cost") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "ko_hedge");

  let hedgeDelta = -_this.hedgeDataRaw
    .map((d) => d.delta * d.notional)
    .reduce(function(a, b) {
      return a + b;
    }, 0);

  let program = programTotal(_this.programData, _this.expoDataRaw);

  _this.totalExpoHedges = [
    {
      date: "BALANCE",
      expo: expoBalance,
      undated: undatedBalance,
      hedge: hedgeDelta,
      hedgeP:
        expoBalance !== 0 ? formatPercentSm(hedgeDelta / expoBalance) : "-",
      hedPPos: 0,
      program: program,
      programP:
        expoBalance !== 0 ? formatPercentSm(program / expoBalance) : "-",
    },
    {
      date: "CASH FLOW",
      expo: expoCF,
      projected: projected,
      hedge: hedge,
      hedgeP: expoCF !== 0 ? formatPercentSm(hedge / expoCF) : "-",
      hedPPos: 0,
      program: program,
      programP: expoCF !== 0 ? formatPercentSm(program / expoCF) : "-",
    },
  ];

  let unlHedge =
    addByField(_this.hedgeDataRaw, "hedgeNotional", "hedge") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "hedge_and_reduce_cost") -
    addByField(_this.hedgeDataRaw, "hedgeNotional", "cap_hedge");
  let limHedge =
    addByField(_this.hedgeDataRaw, "hedgeNotional", "limited_hedge") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "cap_hedge");
  let koHedge = addByField(_this.hedgeDataRaw, "hedgeNotional", "ko_hedge");

  let unlParticipation =
    addByField(_this.hedgeDataRaw, "hedgeNotional", "hedge") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "limited_hedge") +
    addByField(_this.hedgeDataRaw, "hedgeNotional", "ko_hedge");
  let limParticipation = 0;
  let noParticipation = addByField(
    _this.hedgeDataRaw,
    "hedgeNotional",
    "hedge_and_reduce_cost",
  );
  let addPart = addByField(
    _this.hedgeDataRaw,
    "hedgeNotional",
    "provide_participation",
  );
  let reduceCost = addByField(
    _this.hedgeDataRaw,
    "hedgeNotional",
    "reduce_cost",
  );
  let leverage = 0;
  let reBalance = 0;
  if (hedge > 0) {
    // step 1: add participation
    reBalance = Math.min(addPart, noParticipation);
    unlParticipation += reBalance;
    noParticipation -= reBalance;
    addPart -= reBalance;
    // step 2: add reduce cost
    reBalance = Math.min(reduceCost, unlParticipation);
    unlParticipation -= reBalance;
    limParticipation += reBalance;
    reduceCost -= reBalance;
    // step 3: remainder for addPart and reduceCost
    addPart = Math.max(0, addPart - reduceCost);
    reduceCost = Math.max(0, reduceCost - addPart);
    if (addPart > 0) {
      reBalance = Math.min(addPart, noParticipation);
      unlParticipation += reBalance;
      noParticipation -= reBalance;
      addPart -= reBalance;
      reBalance = Math.min(addPart, limParticipation);
      unlParticipation += reBalance;
      limParticipation -= reBalance;
    }
    leverage = reduceCost;
  } else if (hedge < 0) {
    // step 1: add participation
    reBalance = Math.max(addPart, noParticipation);
    unlParticipation += reBalance;
    noParticipation -= reBalance;
    addPart -= reBalance;
    // step 2: add reduce cost
    reBalance = Math.max(reduceCost, unlParticipation);
    unlParticipation -= reBalance;
    limParticipation += reBalance;
    reduceCost -= reBalance;
    // step 3: remainder for addPart and reduceCost
    addPart = Math.min(0, addPart - reduceCost);
    reduceCost = Math.min(0, reduceCost - addPart);
    if (addPart > 0) {
      reBalance = Math.max(addPart, noParticipation);
      unlParticipation += reBalance;
      noParticipation -= reBalance;
      addPart -= reBalance;
      reBalance = Math.max(addPart, limParticipation);
      unlParticipation += reBalance;
      limParticipation -= reBalance;
    }
    leverage = reduceCost;
  }

  unlParticipation = Number.isFinite(unlParticipation) ? unlParticipation : 0;
  limParticipation = Number.isFinite(limParticipation) ? limParticipation : 0;
  noParticipation = Number.isFinite(noParticipation) ? noParticipation : 0;
  leverage = Number.isFinite(leverage) ? leverage : 0;

  _this.detailExpoHedges = [
    { y: "LEVERAGE", x: leverage },
    { y: "UNLIM. PARTICIPATION", x: unlParticipation },
    { y: "LIMITED PARTICIPATION", x: limParticipation },
    { y: "NO PARTICIPATION", x: noParticipation },
    { y: "KNOCK OUT HEDGE", x: koHedge },
    { y: "CAPPED HEDGE", x: limHedge },
    { y: "UNLIMITED HEDGE", x: unlHedge },
    { y: "CASH FLOW EXPOSURE", x: expoCF },
    { y: "BALANCE EXPOSURE", x: expoBalance },
  ];

  let num =
    (5 *
      (expoCF -
        (unlHedge +
          0.5 * limHedge +
          0.25 * koHedge +
          unlParticipation +
          0.5 * limParticipation +
          0.25 * noParticipation))) /
    expoCF;
  _this.riskProfile = riskProfile(num);

  _this.portRisk =
    num < 1.5
      ? _this.signal1
      : num < 2.5
      ? _this.signal2
      : num < 3.5
      ? _this.signal3
      : num < 4.5
      ? _this.signal4
      : _this.signal5;

  _this.hedge = hedge;
  _this.program = program;
}
// ===== totals for portfolio chart ===== //
export function calculateExpoHedgesCommodity(_this) {
  function addByField(data, field, filterField) {
    return data
      .map((d) =>
        d.derivative_purpose === filterField
          ? d[field] * d.undelying_position
          : 0,
      )
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  }
  function riskProfile(num) {
    if (num < 1.5) {
      return "VERY CONSERVATIVE";
    } else if (num < 2.5) {
      return "CONSERVATIVE";
    } else if (num < 3.5) {
      return "BALANCED";
    } else if (num < 4.5) {
      return "AGGRESSIVE";
    } else {
      return "VERY AGGRESSIVE";
    }
  }
  let expoData, hedgeData;
  if (_this.xRange.length === 0) {
    expoData = _this.expoDataRaw;
    hedgeData = _this.hedgeDataRaw;
  } else {
    let initDate = _this.xRange[_this.zoomInitial];
    let finalDate =
      _this.xRange[Math.min(_this.xRange.length - 1, _this.zoomFinal + 1)];
    expoData = _this.expoDataRaw.filter(
      (d) =>
        +d[_this.expoDateField] >= +initDate &&
        +d[_this.expoDateField] < +finalDate,
    );
    hedgeData = _this.hedgeDataRaw.filter(
      (d) =>
        +d[_this.hedgeDateField] >= +initDate &&
        +d[_this.hedgeDateField] < +finalDate,
    );
  }

  let forecasted, recognized, program;
  if (_this.isCurrency) {
    forecasted = expoData
      .map((d) => d.cash_flow)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
    recognized = expoData
      .map((d) => d.balance)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
    program = expoData
      .map((d) => d.program)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  } else {
    let forecasted_au = expoData
      .map((d) => d.cash_flow)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
    let recognized_au = expoData
      .map((d) => d.balance)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
    // forecasted = expoData
    //   .map((d) => d.cash_flow * d.ratio * d.unit_change)
    //   .reduce(function(a, b) {
    //     return a + b;
    //   }, 0);
    recognized = expoData
      .map((d) => d.balance * d.ratio * d.unit_change)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
    program = expoData
      .map((d) => d.program * d.ratio * d.unit_change)
      .reduce(function(a, b) {
        return a + b;
      }, 0);
    forecasted = (recognized * forecasted_au) / recognized_au;
  }
  let sign = Math.sign(forecasted);
  sign = sign === 0 ? Math.sign(recognized) : sign;
  sign = sign === 0 ? 1 : sign;
  forecasted *= sign;
  recognized *= sign;
  program *= sign;

  let expo = forecasted;
  let hedge = hedgeData
    .map((d) => d.hedgeNotional)
    .reduce(function(a, b) {
      return a + b;
    }, 0);
  hedge *= sign;

  let unlHedge =
    addByField(hedgeData, "hedgeNotional", "hedge") +
    addByField(hedgeData, "hedgeNotional", "hedge_and_reduce_cost") -
    sign * addByField(hedgeData, "notional", "cap_hedge");
  let limHedge =
    addByField(hedgeData, "hedgeNotional", "limited_hedge") +
    sign * addByField(hedgeData, "notional", "cap_hedge");
  let koHedge = addByField(hedgeData, "hedgeNotional", "ko_hedge");
  // unlHedge *= sign;
  // limHedge *= sign;
  // koHedge *= sign;

  let unlParticipation =
    addByField(hedgeData, "hedgeNotional", "hedge") +
    addByField(hedgeData, "hedgeNotional", "ko_hedge");
  let limParticipation = addByField(
    hedgeData,
    "hedgeNotional",
    "limited_hedge",
  );
  let noParticipation = addByField(
    hedgeData,
    "hedgeNotional",
    "hedge_and_reduce_cost",
  );
  let addPart =
    sign * addByField(hedgeData, "notional", "provide_participation");
  let reduceCost =
    sign *
    (addByField(hedgeData, "notional", "reduce_cost") +
      addByField(hedgeData, "notional", "reduce_cost_leverage"));
  let leverage = 0;
  let reBalance = 0;

  if (hedge > 0) {
    // step 1: add participation
    reBalance = Math.min(addPart, noParticipation);
    unlParticipation += reBalance;
    noParticipation -= reBalance;
    addPart -= reBalance;
    // step 2: add reduce cost
    reBalance = Math.min(reduceCost, unlParticipation);
    unlParticipation -= reBalance;
    limParticipation += reBalance;
    reduceCost -= reBalance;
    // step 3: remainder for addPart and reduceCost
    addPart = Math.max(0, addPart - reduceCost);
    reduceCost = Math.max(0, reduceCost - addPart);
    if (addPart > 0) {
      reBalance = Math.min(addPart, noParticipation);
      unlParticipation += reBalance;
      noParticipation -= reBalance;
      addPart -= reBalance;
      reBalance = Math.min(addPart, limParticipation);
      unlParticipation += reBalance;
      limParticipation -= reBalance;
    }
    leverage = reduceCost;
  } else if (hedge < 0) {
    // step 1: add participation
    reBalance = Math.max(addPart, noParticipation);
    unlParticipation += reBalance;
    noParticipation -= reBalance;
    addPart -= reBalance;
    // step 2: add reduce cost
    reBalance = Math.max(reduceCost, unlParticipation);
    unlParticipation -= reBalance;
    limParticipation += reBalance;
    reduceCost -= reBalance;
    // step 3: remainder for addPart and reduceCost
    addPart = Math.min(0, addPart - reduceCost);
    reduceCost = Math.min(0, reduceCost - addPart);
    if (addPart > 0) {
      reBalance = Math.max(addPart, noParticipation);
      unlParticipation += reBalance;
      noParticipation -= reBalance;
      addPart -= reBalance;
      reBalance = Math.max(addPart, limParticipation);
      unlParticipation += reBalance;
      limParticipation -= reBalance;
    }
    leverage = reduceCost;
  }

  unlParticipation = Number.isFinite(unlParticipation) ? unlParticipation : 0;
  limParticipation = Number.isFinite(limParticipation) ? limParticipation : 0;
  noParticipation = Number.isFinite(noParticipation) ? noParticipation : 0;
  leverage = Number.isFinite(leverage) ? leverage : 0;
  const basisRatioStr = _this.fixing_type
    ? "Commited " + _this.fixing_type
    : "Commited";

  _this.detailExpoHedges = [
    { y: "Leverage", x: leverage, sign: sign }, // 0
    { y: "Unlimited part.", x: unlParticipation }, // 1
    { y: "Limited part.", x: limParticipation }, // 2
    { y: "No participation", x: noParticipation }, // 3
    { y: "Knock out hedge", x: koHedge }, // 4
    { y: "Capped hedge", x: limHedge }, // 5
    { y: "Unlimited hedge", x: unlHedge }, // 6
    { y: basisRatioStr, x: recognized }, // 7
    { y: "Forecasted", x: forecasted }, // 8
  ];

  let num =
    (5 *
      (expo -
        (unlHedge +
          0.5 * limHedge +
          0.25 * koHedge +
          unlParticipation +
          0.5 * limParticipation +
          0.25 * noParticipation))) /
    expo;
  _this.riskProfile = riskProfile(num);

  _this.portRisk =
    num < 1.5
      ? _this.signal1
      : num < 2.5
      ? _this.signal2
      : num < 3.5
      ? _this.signal3
      : num < 4.5
      ? _this.signal4
      : _this.signal5;

  _this.hedge = hedge;
  _this.program = program;
}
// ===== base currency ===== //
export function baseCurrencyFn(_this) {
  _this.baseCurrency = {
    rate: "N.A.",
    foreign: "N.A.",
    base: "N.A.",
    changeBase: "N.A.",
  };
  if (_this.expoDataRaw.length > 0) {
    let curr = _this.expoDataRaw[0].currency;
    _this.baseCurrency.rate = _this.expoDataRaw[0].currency;
    _this.baseCurrency.foreign =
      "foreign_currency" in Object.keys(_this.expoDataRaw[0])
        ? _this.expoDataRaw[0].foreign_currency
        : curr.slice(0, 3);
    if (curr.slice(0, 3) === _this.baseCurrency.foreign) {
      _this.baseCurrency.base = curr.slice(3, 6);
    } else {
      _this.baseCurrency.base = curr.slice(0, 3);
    }
  } else if (_this.hedgeDataRaw.length > 0) {
    let curr = _this.hedgeDataRaw[0].currency;
    _this.baseCurrency.rate = _this.hedgeDataRaw[0].currency;
    _this.baseCurrency.foreign =
      "foreign_currency" in Object.keys(_this.hedgeDataRaw[0])
        ? _this.expoDataRaw[0].foreign_currency
        : curr.slice(0, 3);
    if (curr.slice(0, 3) === _this.baseCurrency.foreign) {
      _this.baseCurrency.base = curr.slice(3, 6);
    } else {
      _this.baseCurrency.base = curr.slice(0, 3);
    }
  }
}
// ===== business logic ===== //
// forwards
export function calculateExpoForward(_this) {
  if (_this.pickedAnalysis <= 1 || _this.pickedAnalysis === 4) {
    _this.expoDataRaw.map((d) =>
      d.time_to_expiration > 0
        ? (d.fwd = _this.spot * Math.exp((d.r - d.rf) * d.time_to_expiration))
        : (d.fwd = NaN),
    );
  } else {
    _this.expoDataRaw.map((d) =>
      d.time_to_expiration > 0 ? (d.fwd = _this.spot) : (d.fwd = NaN),
    );
  }
}
// forwards for hedges
export function calculateHedgeForward(_this) {
  if (_this.hedgeDataRaw.length > 0) {
    if (_this.pickedAnalysis <= 1 || _this.pickedAnalysis === 4) {
      _this.hedgeDataRaw.map(
        (d) =>
          (d.fwd =
            d.time > 0 ? _this.spot * Math.exp((d.r - d.rf) * d.time) : NaN),
      );
    } else {
      _this.hedgeDataRaw.map((d) => (d.fwd = d.time > 0 ? _this.spot : NaN));
    }
  }
}
// balance expo
export function calculateBalanceExpo(
  data,
  portfolio,
  all = true,
  initialDate = null,
  endDate = null,
) {
  if (all) {
    return data
      .map((d) => (d.portfolio === portfolio ? d.balance + d.undated : 0))
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  } else {
    return data
      .map((d) =>
        d.portfolio === portfolio && d.date > initialDate && d.date <= endDate
          ? d.balance + d.undated
          : 0,
      )
      .reduce(function(a, b) {
        return a + b;
      }, 0);
  }
}
// cash flow expo
export function calculateCashFlowExpo(data, portfolio, initDate, lastDate) {
  return data
    .map((d) =>
      d.portfolio === portfolio && d.date > initDate && d.date <= lastDate
        ? d.balance + d.cash_flow
        : 0,
    )
    .reduce(function(a, b) {
      return a + b;
    }, 0);
}
// ===== program ===== //
export function programEntry(lastDate, program, cashFlow, portfolio) {
  return {
    date: lastDate,
    trade_date: 0,
    cash_flow: 0,
    balance: 0,
    fwd: 0,
    hedge: 0,
    hedgeValue: 0,
    premium: 0,
    program: program - cashFlow,
    portfolio: portfolio,
  };
}
export function createProgramForBucketBalance(
  expoData,
  program,
  portfolio,
  today,
  lastDate,
) {
  let useDefault = false;
  let prtfl;
  if (expoData.length === 0) {
    useDefault = true;
    prtfl = "N.A";
  } else {
    if (expoData[0].date > lastDate) {
      useDefault = true;
      prtfl = expoData[0].portfolio;
    }
  }
  if (useDefault) {
    let item = [
      {
        date: lastDate,
        trade_date: 0,
        cash_flow: 0,
        balance: 0,
        fwd: 0,
        hedge: 0,
        hedgeValue: 0,
        premium: 0,
        program: program,
        portfolio: prtfl,
        undated: 0,
        newHedge: 0,
      },
    ];
    expoData = item.concat(expoData);
  } else {
    expoData.forEach(function(dat) {
      if (
        dat.portfolio === portfolio &&
        dat.date > today &&
        dat.date <= lastDate
      ) {
        dat.program = program;
      }
    });
  }
  return expoData;
}
export function createProgramForBucket(
  expoData,
  program,
  portfolio,
  today,
  lastDate,
) {
  let cashFlow = calculateCashFlowExpo(expoData, portfolio, today, lastDate);
  let percentage = program / cashFlow;
  expoData.forEach(function(dat) {
    if (
      dat.portfolio === portfolio &&
      dat.date > today &&
      dat.date <= lastDate
    ) {
      dat.program = percentage * (dat.balance + dat.cash_flow);
    }
  });
}

export function nExposures(expoData, portfolio, today, lastDate) {
  let num = 0;
  expoData.forEach(function(dat) {
    if (
      dat.portfolio === portfolio &&
      dat.date > today &&
      dat.date <= lastDate
    ) {
      num += 1;
    }
  });
  return num;
}

// export function includeProgramNotional(expoData, program) {
//   let today;
//   let lastDate;

//   program.forEach(function(d) {
//     if (typeof d.flow != "object") {
//       d.flow = JSON.parse(d.flow);
//     }
//     let portfolio = d.fk_idportfolio;
//     if (d.objective.toUpperCase() === "BALANCE") {
//       let program2Assign = calculateBalanceExpo(expoData, portfolio);
//       let months;
//       if (d.parameters.toUpperCase() === "MATCH") {
//         today = new Date();
//         lastDate = max(expoData, function(item) {
//           return item.date !== null ? item.date : today;
//         });
//         program2Assign = (d.flow.values.percentage / 100) * program2Assign;
//         let cashFlow = sum(expoData, function(item) {
//           return Number.isFinite(item.balance) && item.balance != null
//             ? item.balance
//             : 0;
//         });
//         let percentage = Math.min(1, program2Assign / cashFlow);
//         expoData.forEach(function(dat) {
//           if (
//             dat.portfolio === portfolio &&
//             dat.date > today &&
//             dat.date <= lastDate
//           ) {
//             dat.program = percentage * dat.balance;
//           }
//         });
//         if (
//           (cashFlow >= 0 && program2Assign > cashFlow) ||
//           (cashFlow < 0 && program2Assign < cashFlow)
//         ) {
//           expoData.push(
//             programEntry(lastDate, program2Assign, cashFlow, portfolio),
//           );
//         }
//       } else if (d.parameters.toUpperCase() === "CONSTANT") {
//         months = d.flow.values.months;
//         program2Assign =
//           (program2Assign * d.flow.values.percentage) / 100 / months;
//         today = new Date();
//         lastDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
//         let i = 0;
//         while (i < months) {
//           createProgramForBucket(
//             expoData,
//             program2Assign,
//             portfolio,
//             today,
//             lastDate,
//           );
//           today = lastDate;
//           lastDate = new Date(
//             lastDate.getFullYear(),
//             lastDate.getMonth() + 2,
//             0,
//           );
//           i += 1;
//         }
//       } else if (d.parameters.toUpperCase() === "MONTHLY") {
//         let num = 0;
//         months = d.flow.values;
//         today = new Date();
//         lastDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
//         for (const i in months) {
//           num = nExposures(expoData, portfolio, today, lastDate);
//           if (num === 0) {
//             expoData = createProgramForBucketBalance(
//               expoData,
//               (months[i] * program2Assign) / 100,
//               portfolio,
//               today,
//               lastDate,
//             );
//           } else {
//             createProgramForBucket(
//               expoData,
//               (months[i] * program2Assign) / 100,
//               portfolio,
//               today,
//               lastDate,
//             );
//           }
//           today = lastDate;
//           lastDate = new Date(
//             lastDate.getFullYear(),
//             lastDate.getMonth() + 2,
//             0,
//           );
//         }
//       } else if (d.parameters.toUpperCase() === "QUARTERLY") {
//         let num = 0;
//         let quarters = d.flow.values;
//         today = new Date();
//         lastDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);
//         for (const i in quarters) {
//           num = nExposures(expoData, portfolio, today, lastDate);
//           if (num === 0) {
//             expoData = createProgramForBucketBalance(
//               expoData,
//               (quarters[i] * program2Assign) / 100,
//               portfolio,
//               today,
//               lastDate,
//             );
//           } else {
//             createProgramForBucket(
//               expoData,
//               (quarters[i] * program2Assign) / 100,
//               portfolio,
//               today,
//               lastDate,
//             );
//           }
//           today = lastDate;
//           lastDate = new Date(
//             lastDate.getFullYear(),
//             lastDate.getMonth() + 4,
//             0,
//           );
//         }
//       }
//     } else {
//       if (d.parameters.toUpperCase() === "CONSTANT") {
//         today = new Date();
//         lastDate = new Date(
//           today.getFullYear(),
//           today.getMonth() + d.flow.values.months + 1,
//           0,
//         );
//         let perc = d.flow.values.percentage;
//         expoData.forEach(function(item) {
//           if (item.portfolio === portfolio && item.date <= lastDate) {
//             item.program = ((item.balance + item.cash_flow) * perc) / 100;
//           }
//         });
//       } else if (d.parameters.toUpperCase() === "MONTHLY") {
//         today = new Date();
//         lastDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
//         let months = d.flow.values;
//         for (const i in months) {
//           expoData.forEach(function(item) {
//             if (
//               item.portfolio === portfolio &&
//               item.date > today &&
//               item.date <= lastDate
//             ) {
//               item.program =
//                 ((item.balance + item.cash_flow) * months[i]) / 100;
//             }
//           });
//           today = lastDate;
//           lastDate = new Date(
//             lastDate.getFullYear(),
//             lastDate.getMonth() + 2,
//             0,
//           );
//         }
//       } else if (d.parameters.toUpperCase() === "QUARTERLY") {
//         today = new Date();
//         lastDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);
//         let quarters = d.flow.values;
//         for (const i in quarters) {
//           expoData.forEach(function(item) {
//             if (
//               item.portfolio === portfolio &&
//               item.date > today &&
//               item.date <= lastDate
//             ) {
//               item.program =
//                 ((item.balance + item.cash_flow) * quarters[i]) / 100;
//             }
//           });
//           today = lastDate;
//           lastDate = new Date(
//             lastDate.getFullYear(),
//             lastDate.getMonth() + 4,
//             0,
//           );
//         }
//       } else if (d.parameters.toUpperCase() === "BUDGET") {
//         today = new Date();
//         lastDate = new Date(
//           today.getFullYear(),
//           d.flow.values.percentagemonths - 1,
//           0,
//         );
//         let monthDist =
//           12 * (lastDate.getFullYear() - today.getFullYear()) +
//           (lastDate.getMonth() - today.getMonth());
//         while (monthDist < 3) {
//           lastDate = new Date(
//             lastDate.getFullYear(),
//             lastDate.getMonth() + 13,
//             0,
//           );
//           monthDist =
//             12 * (lastDate.getFullYear() - today.getFullYear()) +
//             (lastDate.getMonth() - today.getMonth());
//         }
//         let perc = d.flow.values.percentage;
//         expoData.forEach(function(item) {
//           if (item.portfolio === portfolio && item.date <= lastDate) {
//             item.program = ((item.balance + item.cash_flow) * perc) / 100;
//           }
//         });
//       }
//     }
//   });
//   return expoData;
// }
// export function includePastProgramNotional(expoData, program) {
//   let portfolio;
//   let percentage;
//   let months;
//   program.forEach(function(d) {
//     portfolio = d.fk_idportfolio;
//     if (typeof d.flow != "object") {
//       d.flow = JSON.parse(d.flow);
//     }

//     if (d.objective.toUpperCase() === "BALANCE") {
//       percentage = 0;
//       if (
//         d.parameters.toUpperCase() === "MATCH" ||
//         d.parameters.toUpperCase() === "CONSTANT"
//       ) {
//         percentage = d.flow.values.percentage / 100;
//       } else if (
//         d.parameters.toUpperCase() === "MONTHLY" ||
//         d.parameters.toUpperCase() === "QUARTERLY"
//       ) {
//         months = d.flow.values;
//         for (const i in months) {
//           percentage += months[i];
//         }
//         percentage = percentage / 100;
//       }
//     } else {
//       if (
//         d.parameters.toUpperCase() === "BUDGET" ||
//         d.parameters.toUpperCase() === "CONSTANT"
//       ) {
//         percentage = d.flow.values.percentage / 100;
//       } else if (
//         d.parameters.toUpperCase() === "MONTHLY" ||
//         d.parameters.toUpperCase() === "QUARTERLY"
//       ) {
//         months = d.flow.values;
//         percentage = months[0] / 100;
//       }
//     }
//   });
//   expoData.forEach(function(dat) {
//     dat.program =
//       dat.portfolio === portfolio
//         ? (dat.balance + dat.cash_flow) * percentage
//         : 0;
//   });
//   return expoData;
// }

// ===== spot ===== //
export function spotFromFutHedge(
  _this,
  asset = "currency",
  fieldShowLoading = "showLoadingSpot",
) {
  _this[fieldShowLoading] = true;
  if (
    !_this.allDataRaw ||
    _this.allDataRaw.length === 0 ||
    _this.allDataRaw[0] == null
  ) {
    _this[fieldShowLoading] = false;
    return;
  }
  let spot = null;
  let hLen = _this.allDataRaw[0]["hedge"].length;
  if (hLen > 0) {
    spot =
      _this.allDataRaw[0]["hedge"].find((obj) => obj.spot != null)?.spot ??
      null;
    _this.spot = spot;
    _this.simRate = spot;
    _this.prevSpot = spot;
  }

  if (spot == null) {
    if (asset === "currency") {
      getSpotData(_this, "spot", "updatedAt", fieldShowLoading)
        .then((data) => {
          const isEndpointBack = _this.faildGetHistoricalData;
          data = isEndpointBack ? data[0] : data;
          _this["spotData"] = data;
          const observationDateTime = `${data["time"]}`;
          const dateObj = new Date(observationDateTime);
          _this["updatedAt"] = "Updated at: " + formatDate_DDMMMYHMS(dateObj);

          if (isEndpointBack) {
            _this["spot"] = Number(data.close);
          } else {
            _this["spot"] = data.close;
          }
          _this[fieldShowLoading] = false;
        })
        .catch(function(error) {
          _this[fieldShowLoading] = false;
          console.error(error);
        });
    } else if (asset === "commodity") {
      getSpotData(_this, "spot", "updatedAt", fieldShowLoading, "commodity", {
        commodityNumber: _this.commodityNumber,
        commodityContract: _this.contract,
      })
        .then((data) => {
          data = typeof data === "string" ? JSON.parse(data) : data;
          const isEndpointBack = _this.faildGetHistoricalData;
          data = isEndpointBack ? data[0] : data;
          _this["spotData"] = data;
          const observationDateTime = isEndpointBack
            ? data.last_observation_date
            : `${data["observation_date"]}T${data["observation_time"]}`;
          const dateObj = new Date(observationDateTime);
          _this["updatedAt"] = "Updated at: " + formatDate_DDMMMYHMS(dateObj);

          if (isEndpointBack) {
            _this["spot"] = data.last_price;
          } else {
            _this["spot"] = data.close;
          }
          _this[fieldShowLoading] = false;
        })
        .catch(function(error) {
          _this[fieldShowLoading] = false;
          console.error(error);
        });
    }
  }
  _this[fieldShowLoading] = false;
  _this.spot = _this.spot === 0 ? null : _this.spot;
  _this.simRate = spot;
  _this.prevSpot = spot;
}

// ===== charts data ===== //
export function createChartsDataRaw(
  _this,
  expoField,
  hedgeField,
  resultField,
  futureBool,
) {
  let dataRaw = [];
  if (futureBool === true) {
    getFinalDateExpoHedge(_this);
    dataRaw = futureExpoData(_this);
  } else {
    dataRaw = []; //pastExpoData(_this);
  }
  let intervalGroup = _this.pickedBucket;
  let today = timeCompare(_this.today, intervalGroup, _this[expoField]);
  today.setDate(today.getDate() - 1); // subtract one day
  let dat = [];
  if (_this[expoField].length > 0) {
    _this[expoField] = sortByKey(_this[expoField], "date");
    dat = _this[expoField].map(function(d) {
      return {
        balance: +d.balance,
        budget_expo: d.budget_expo,
        budget_rate: d.budget_futures,
        cash_flow: +d.cash_flow,
        date: d[_this.expoDateField] !== null ? d[_this.expoDateField] : today,
        delta: 0,
        fixed: 0,
        fwd: futureBool ? +d.fwd : +d.settlement_rate,
        hedge: 0,
        hedgeValue: 0,
        newHedge: 0,
        notional: 0,
        portfolio: d.portfolio,
        premium: 0,
        program: +d.program,
        ratio: d.ratio ? +d.ratio : 1,
        trade_date: 0,
        undated: d.undated,
        unit_change: d.unit_change ? +d.unit_change : 1,
      };
    });
  }

  let dat2 = [];
  if (_this[hedgeField].length > 0) {
    dat2 = _this[hedgeField].map(function(d) {
      return {
        balance: 0,
        budget_expo: 0,
        budget_rate: d.budget_futures,
        cash_flow: 0,
        date: d[_this.hedgeDateField],
        delta: d.delta,
        fixed:
          d.derivative == null || d.derivative == undefined
            ? 0
            : d.derivative.toLowerCase() === "forward" ||
              d.derivative.toLowerCase() === "future" ||
              d.derivative.toLowerCase() === "forwards" ||
              d.derivative.toLowerCase() === "futures"
            ? d.hedgeNotional
            : 0,
        fwd: futureBool ? d.fwd : d.settlement_rate,
        hedge: d.hedgeNotional ? d.hedgeNotional : 0,
        hedgeValue: d.hedge_value,
        newHedge: 0,
        notional: d.notional,
        portfolio: "",
        premium: d.premium,
        program: 0,
        ratio: d.ratio ? +d.ratio : 1,
        trade_date: d.trade_date,
        undated: 0,
        unit_change: d.unit_change ? +d.unit_change : 1,
      };
    });
  }
  _this[resultField] = dat.concat(dat2.concat(dataRaw));
  _this[resultField] = sortByKey(_this[resultField], "date");
}

// ===== user ===== //
export function zoomOnRangeSlider(_this) {
  _this.minMaxYAxisRates = [+Infinity, -Infinity];
  _this.minMaxYAxisFutureRates = [+Infinity, -Infinity];
  Object.assign(_this, {
    chartsDataSliced: _this.chartsData.slice(
      _this.zoomInitial,
      _this.zoomFinal + 1,
    ),
  });
  Object.assign(_this, {
    simulationsSliced: _this.simulations.slice(
      _this.zoomInitial,
      _this.zoomFinal + 1,
    ),
  });

  let initDate = _this.xRange[_this.zoomInitial];
  let finalDate = setMax(
    _this.chartsDataSliced[_this.chartsDataSliced.length - 1],
    _this.xRange,
    _this.pickedIntervalHistory,
  );
  Object.assign(_this, {
    hedgeDataSliced: _this.hedgeDataRaw.filter(
      (d) =>
        +d[_this.derivativeDateField] >= +initDate &&
        +d[_this.derivativeDateField] < +finalDate,
    ),
  });
  minMaxYAxisRatesCalc(
    _this,
    "minMaxYAxisFutureRates",
    "chartsDataSliced",
    "hedgeDataRaw",
    "hedgeDataSliced",
    "simulationsSliced",
  );
  Object.assign(_this, {
    minMaxYAxisRates: [
      Math.min(_this.minMaxYAxisFutureRates[0], _this.minMaxYAxisHistorical[0]),
      Math.max(_this.minMaxYAxisFutureRates[1], _this.minMaxYAxisHistorical[1]),
    ],
  });
  if (_this.showLoadingHistCandles === false) {
    _this.renderHistorical += 1;
  }
}
export function zoomOnRangeSliderPast(_this) {
  _this.minMaxYAxisPast = [+Infinity, -Infinity];
  Object.assign(_this, {
    pastChartsDataSliced: _this.pastChartsDataM.slice(
      _this.zoomInitialPast,
      _this.zoomFinalPast,
    ),
  });
  let initDate = _this.zoomPastXRange[_this.zoomInitialPast];
  let lastDate = _this.zoomPastXRange[_this.zoomFinalPast];
  Object.assign(_this, {
    pastHedgeDataSliced: _this.pastHedgeDataRaw.filter(
      (d) =>
        +d[_this.derivativeDateField] >= +initDate &&
        +d[_this.derivativeDateField] < +lastDate,
    ),
  });
  Object.assign(_this, {
    pastAverages: dataTotalsCurrency(_this.pastChartsDataSliced, _this.isCents),
  });
  if (_this.zoomFinalPast - _this.zoomInitialPast > 48) {
    _this.historicalDataSliced = _this.historicalDataRawM.filter(
      (d) => d.date >= initDate && d.date <= lastDate,
    );
  } else if (_this.zoomFinalPast - _this.zoomInitialPast > 18) {
    _this.historicalDataSliced = _this.historicalDataRawW.filter(
      (d) => d.date >= initDate && d.date <= lastDate,
    );
  } else {
    _this.historicalDataSliced = _this.historicalDataRaw.filter(
      (d) => d.date >= initDate && d.date <= lastDate,
    );
  }

  let hedgingStatsField = "hedgingStats";
  _this[hedgingStatsField] = {
    positive: 0,
    negative: 0,
    neutral: 0,
    posVal: 0,
    negVal: 0,
  };
  let len = 0;
  _this.pastChartsDataSliced.forEach(function(d) {
    if (d.balance !== 0 || d.cash_flow !== 0) {
      len++;
      if (d.hedge_m2m === 0) {
        _this[hedgingStatsField].neutral += 1;
      } else if (d.hedge_m2m > 0) {
        _this[hedgingStatsField].positive += 1;
        _this[hedgingStatsField].posVal += Math.abs(
          noNanNum(d.hedge_rate - d.m2m_rate),
        );
      } else {
        _this[hedgingStatsField].negative += 1;
        _this[hedgingStatsField].negVal -= Math.abs(
          noNanNum(d.hedge_rate - d.m2m_rate),
        );
      }
    }
  });
  _this[hedgingStatsField].posVal /= _this[hedgingStatsField].positive;
  _this[hedgingStatsField].negVal /= _this[hedgingStatsField].negative;
  _this[hedgingStatsField].neutral /= len;
  _this[hedgingStatsField].positive /= len;
  _this[hedgingStatsField].negative /= len;

  minMaxYAxisRatesPastCalc(
    _this,
    "minMaxYAxisPast",
    "pastChartsDataSliced",
    "pastHedgeDataRaw",
    "historicalDataSliced",
    "pastHedgeDataSliced",
  );
}

// ===== simulations ===== //
export function includeSimulations(_this) {
  if (_this.simulations.length > 0) {
    let idx = _this.simulations.map((d) => d.id);
    let id;
    _this.chartsData.forEach(function(d) {
      id = idx.indexOf(d.id);
      if (id > -1) {
        d.hedge_m2m_dn = _this.simulations[id]["hedge_m2m_dn"];
        d.hedge_rate_dn = _this.simulations[id]["hedge_rate_dn"];
        d.m2m_dn = _this.simulations[id]["m2m_dn"];
        d.m2m_rate_dn = _this.simulations[id]["m2m_rate_dn"];
        d.net_m2m_dn = _this.simulations[id]["net_m2m_dn"];
        d.net_rate_dn = _this.simulations[id]["net_rate_dn"];
        d.hedge_m2m_up = _this.simulations[id]["hedge_m2m_up"];
        d.hedge_rate_up = _this.simulations[id]["hedge_rate_up"];
        d.m2m_up = _this.simulations[id]["m2m_up"];
        d.m2m_rate_up = _this.simulations[id]["m2m_rate_up"];
        d.net_m2m_up = _this.simulations[id]["net_m2m_up"];
        d.net_rate_up = _this.simulations[id]["net_rate_up"];
      }
    });
  }
}
// export function createSimulationScenario(_this, val) {
//   let m2m_rate = val === "down" ? "percentiles_dn" : "percentiles_up";
//   let expoSims = _this.simulationsRaw.filter(d => d.is_expo);
//   let pkx = expoSims.map(d => d.pk);
//   let pk;
//   _this.expoDataRaw.forEach(function(d) {
//     pk = pkx.indexOf(d.pk);
//     if (pk > -1) {
//       d.fwd = expoSims[pk][m2m_rate];
//     }
//   });
//   let hedgeSims = _this.simulationsRaw.filter(d => !d.is_expo);
//   pkx = hedgeSims.map(d => d.pk);
//   _this.hedgeDataRaw.forEach(function(d) {
//     pk = pkx.indexOf(d.pk);
//     if (pk > -1) {
//       d.fwd = hedgeSims[pk][m2m_rate];
//     }
//   });
// }

export function renderTopographyCharts(_this) {
  Object.assign(_this, {
    derivativeAveragesSliced: _this.derivativeAverages.slice(
      _this.zoomInitial,
      _this.zoomFinal + 1,
    ),
  });
  Object.assign(_this, {
    chartDataTotals: dataTotalsCurrency(_this.chartsDataSliced, _this.isCents),
  });
  _this.dateFormat = tFormatAnalytics[_this.pickedBucket];
  let initDate = _this.xRange[_this.zoomInitial];
  let finalDate =
    _this.xRange[Math.min(_this.xRange.length - 1, _this.zoomFinal + 1)];
  let hDataSliced = _this.hedgeDataRaw.filter(
    (d) => d.date >= initDate && d.date < finalDate,
  );
  let dAve = dataTotalsDerivativesCurrency(hDataSliced);
  _this.averages["lFwd"] = dAve["lFwd"];
  _this.averages["sFwd"] = dAve["sFwd"];
  _this.averages["lCall"] = dAve["lCall"];
  _this.averages["sCall"] = dAve["sCall"];
  _this.averages["lPut"] = dAve["lPut"];
  _this.averages["sPut"] = dAve["sPut"];

  _this.averages["lFwdDelta"] = dAve["lFwdDelta"];
  _this.averages["sFwdDelta"] = dAve["sFwdDelta"];
  _this.averages["lCallDelta"] = dAve["lCallDelta"];
  _this.averages["sCallDelta"] = dAve["sCallDelta"];
  _this.averages["lPutDelta"] = dAve["lPutDelta"];
  _this.averages["sPutDelta"] = dAve["sPutDelta"];

  _this.averages["notionalLFwd"] = dAve["notionalLFwd"];
  _this.averages["notionalSFwd"] = dAve["notionalSFwd"];
  _this.averages["notionalLCall"] = dAve["notionalLCall"];
  _this.averages["notionalSCall"] = dAve["notionalSCall"];
  _this.averages["notionalLPut"] = dAve["notionalLPut"];
  _this.averages["notionalSPut"] = dAve["notionalSPut"];

  if (_this.simulationsRaw.length > 0) {
    let simsAve = dataTotalsCurrencySims(_this.simulationsRaw, _this.isCents);
    _this.averages["m2m_rate_dn"] = simsAve["m2m_rate_dn"];
    _this.averages["m2m_rate_up"] = simsAve["m2m_rate_up"];
    _this.averages["hedge_rate_dn"] = simsAve["hedge_rate_dn"];
    _this.averages["hedge_rate_up"] = simsAve["hedge_rate_up"];
    _this.averages["net_rate_dn"] = simsAve["net_rate_dn"];
    _this.averages["net_rate_up"] = simsAve["net_rate_up"];
    _this.averages["m2m_dn"] = simsAve["m2m_dn"];
    _this.averages["m2m_up"] = simsAve["m2m_up"];
    _this.averages["hedge_m2m_dn"] = simsAve["hedge_m2m_dn"];
    _this.averages["hedge_m2m_up"] = simsAve["hedge_m2m_up"];
    _this.averages["net_m2m_up"] = simsAve["net_m2m_up"];
    _this.averages["net_m2m_dn"] = simsAve["net_m2m_dn"];
  } else {
    _this.averages["m2m_rate_dn"] = NaN;
    _this.averages["m2m_rate_up"] = NaN;
    _this.averages["hedge_rate_dn"] = NaN;
    _this.averages["hedge_rate_up"] = NaN;
    _this.averages["net_rate_dn"] = NaN;
    _this.averages["net_rate_up"] = NaN;
    _this.averages["m2m_dn"] = NaN;
    _this.averages["m2m_up"] = NaN;
    _this.averages["hedge_m2m_dn"] = NaN;
    _this.averages["hedge_m2m_up"] = NaN;
    _this.averages["net_m2m_up"] = NaN;
    _this.averages["net_m2m_dn"] = NaN;
  }

  _this.showLoadingTopographyCalcs = false;
  _this.showLoadingTopographyCalcsRates = false;
  _this.renderPlanSignal += 1;
  _this.renderRateSignal += 1;
  _this.renderM2MSignal += 1;
}

export function sliceMinMaxRenderHistorical(_this) {
  sliceData(
    _this,
    "pickedGroupIntervalHistory",
    "historicalDataRaw",
    "historicalDataRawW",
    "historicalDataRawM",
    "pickedIntervalHistory",
    "historicalData",
    "pastXRange",
    _this.pickedGroupIntervalHistory,
  );
  _this.minMaxYAxisHistorical = _this.processData.minMax(
    _this.historicalData,
    "high",
    "low",
  );
  _this.minMaxYAxisRates = [
    Math.min(_this.minMaxYAxisFutureRates[0], _this.minMaxYAxisHistorical[0]),
    Math.max(_this.minMaxYAxisFutureRates[1], _this.minMaxYAxisHistorical[1]),
  ];
  if (
    _this.showLoadingTopography === false &&
    _this.showLoadingValuating === false
  ) {
    _this.renderRateSignal += 1;
    _this.renderM2MSignal += 1;
  }
  _this.dateFormatHist =
    tFormat[tIntervalsAnalytics[_this.pickedIntervalHistory]];
  _this.showLoadingHistoricalRatesCalcs = false;
  _this.renderHistorical += 1;
}

export function sliceRenderAccum(_this) {
  sliceData(
    _this,
    "pickedGroupIntervalHistory", // tIntervalsAnalytics[interval]
    "pastChartsDataD",
    "pastChartsDataW",
    "pastChartsDataM",
    "pickedIntervalHistory",
    "pastData",
    "pastXRange",
    _this.pickedGroupIntervalHistory,
  );

  _this.showLoadingPastCalcs = false;
  _this.renderAccumSignal += 1;
}

export function latestHedgesRefDate(selectedTime) {
  let refDate;
  if (selectedTime === "WEEK") {
    refDate = new Date(new Date().setDate(new Date().getDate() - 7));
  } else if (selectedTime === "2 WEEKS") {
    refDate = new Date(new Date().setDate(new Date().getDate() - 15));
  } else {
    refDate = new Date(new Date().setMonth(new Date().getMonth() - 1));
  }
  return refDate;
}

export function getLatestHedges(_this) {
  let refDate = latestHedgesRefDate(_this.selectedExecutionTime);
  let latestTrades = _this.hedgeDataRaw.filter((d) => d.trade_date >= refDate);
  _this.chartsData.forEach(function(d) {
    d.last_m_hedge = 0;
  });
  let lenCD = _this.xRange.length;
  let i = 0;
  latestTrades.forEach(function(d) {
    for (i = 0; i < lenCD; i += 1) {
      if (d.date >= _this.xRange[i] && d.date < _this.xRange[i + 1]) {
        _this.chartsData[i].last_m_hedge += d.hedgeNotional;
      }
    }
  });
}

// ===== helpers ===== //
export function hedgeCondition(d) {
  return (
    d === "hedge" ||
    d === "limited_hedge" ||
    d === "hedge_and_reduce_cost" ||
    d === "ko_hedge"
  );
}

export function resetHistoricalOHLC(_this) {
  let nLen = _this.historicalDataRaw.length - 1;
  _this.historicalDataRaw[nLen].open = _this.historicalOHLC.open;
  _this.historicalDataRaw[nLen].high = _this.historicalOHLC.high;
  _this.historicalDataRaw[nLen].low = _this.historicalOHLC.low;
  _this.historicalDataRaw[nLen].close = _this.historicalOHLC.close;
  _this.historicalDataRaw = _this.processData.includeSpot(
    _this.historicalDataRaw,
    _this.spot,
  );
  let dateM =
    _this.historicalDataRawM[_this.historicalDataRawM.length - 1].date;
  let lastM = _this.historicalDataRaw.filter((d) => d.date >= dateM);
  let lenM = _this.historicalDataRawM.length - 1;
  _this.historicalDataRawM[lenM].high = max(lastM, (d) => d.close);
  _this.historicalDataRawM[lenM].low = min(lastM, (d) => d.close);
  _this.historicalDataRawM[lenM].close = _this.spot;

  let dateW =
    _this.historicalDataRawW[_this.historicalDataRawW.length - 1].date;
  let lastW = _this.historicalDataRaw.filter((d) => d.date >= dateW);
  let lenW = _this.historicalDataRawW.length - 1;
  _this.historicalDataRawW[lenW].high = max(lastW, (d) => d.close);
  _this.historicalDataRawW[lenW].low = min(lastW, (d) => d.close);
  _this.historicalDataRawW[lenW].close = _this.spot;
}

export function monthDiff(d1, d2) {
  // Months between two dates
  let months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months += d2.getMonth() - d1.getMonth();
  return months <= 0 ? 0 : months;
}

export function quarterDate(date) {
  // Return the quarter that the variable "date" belongs to, for nesting
  let tod = new Date();
  let mDiff = monthDiff(tod, date);
  let newDate;
  if (mDiff === 0) {
    newDate = timeMonth(date);
  } else {
    let quarters = Math.floor(monthDiff(tod, date) / 3);
    newDate = timeDay(
      timeDay(tod.setMonth(tod.getMonth() + 3 * quarters + 1)).setDate(1),
    );
  }
  return newDate;
}

export function timeCompare(date, intervalGroup, data) {
  // Nesting function, returns the appropriate date for nesting
  let dateCopy;
  let durfn = "";
  if (date) {
    if (intervalGroup === "1D") {
      durfn = timeDay(date);
    } else if (intervalGroup === "1W") {
      durfn = timeMonday(date);
    } else if (intervalGroup === "2W") {
      dateCopy = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date))),
      );
      if (date.getDate() < 15) {
        durfn = timeDay(dateCopy.setDate(1));
      } else {
        durfn = timeDay(dateCopy.setDate(15));
      }
    } else if (intervalGroup === "1M") {
      durfn = timeMonth(date);
    } else if (intervalGroup === "1Q") {
      durfn = quarterDate(date);
    } else if (intervalGroup === "6M") {
      dateCopy = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date))),
      );
      if (dateCopy.getMonth() < 6) {
        durfn = timeDay(timeDay(dateCopy.setMonth(0)).setDate(1));
      } else {
        durfn = timeDay(timeDay(dateCopy.setMonth(6)).setDate(1));
      }
    } else if (intervalGroup === "1Y") {
      if (date.getFullYear() === data[0].date.getFullYear()) {
        durfn = timeDay(
          timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
            JSON.parse(JSON.stringify(data[0].date)),
          ),
        );
      } else {
        durfn = timeYear(date);
      }
    } else if (intervalGroup === "all") {
      durfn = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
          JSON.parse(JSON.stringify(data[0].date)),
        ),
      );
    }
  }
  // if (durfn < data[0].date) {
  //   durfn = timeDay(
  //     timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
  //       JSON.parse(JSON.stringify(data[0].date))
  //     )
  //   );
  // }
  return durfn;
}

export function setMax(data, xRange, intervalGroup) {
  if (xRange.length > 0) {
    let xMax = timeDay(
      timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
        JSON.parse(JSON.stringify(xRange[xRange.length - 1])),
      ),
    );
    if (intervalGroup === "1D") {
      xMax.setDate(xMax.getDate() + 1);
    } else if (intervalGroup === "1W") {
      xMax.setDate(xMax.getDate() + 7);
    } else if (intervalGroup === "2W") {
      xMax.setDate(xMax.getDate() + 15);
    } else if (intervalGroup === "1M") {
      xMax.setMonth(xMax.getMonth() + 1);
    } else if (intervalGroup === "1Q") {
      xMax.setMonth(xMax.getMonth() + 3);
    } else if (intervalGroup === "6M") {
      xMax.setMonth(xMax.getMonth() + 6);
    } else if (intervalGroup === "1Y") {
      xMax.setFullYear(xMax.getFullYear() + 1);
    } else if (intervalGroup === "all") {
      xMax = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
          JSON.parse(JSON.stringify(data[data.length - 1].date)),
        ),
      );
    }
    return timeDay(xMax);
  } else {
    return [];
  }
}

export function add(accumulator, a) {
  return accumulator + a;
}

/**
 * Returns the value if it is a valid finite number; otherwise, returns a fallback value.
 *
 * @param {*} val - The value to evaluate.
 * @param {number} [fallback=0] - The value to return if val is not a finite number.
 * @returns {number}
 */
export function nonNans(val, fallback = 0) {
  // Convert non-number types (e.g., numeric strings, null, undefined) to a number type
  const num = typeof val === "number" ? val : Number(val);

  return Number.isFinite(num) ? num : fallback;
}

export function futureExpoData(_this) {
  let dateList = _this.processData.createWeekByWeekDays(
    _this.initialDate,
    _this.finalDate,
  );
  let dataRaw = [];
  dateList.forEach((d) =>
    dataRaw.push({
      date: d,
      delta: 0,
      notional: 0,
      trade_date: d,
      cash_flow: 0,
      balance: 0,
      fwd: 0,
      hedge: 0,
      hedgeValue: 0,
      premium: 0,
      program: 0,
      portfolio: "",
      undated: 0,
    }),
  );
  return dataRaw;
}

export function pastExpoData(_this) {
  let dataRaw = [];
  _this.historicalDataRaw.forEach((d) =>
    dataRaw.push({
      date: d.date,
      delta: 0,
      notional: 0,
      cash_flow: 0,
      balance: 0,
      fwd: 0,
      hedge: 0,
      hedgeValue: 0,
      premium: 0,
      program: 0,
      portfolio: "",
      undated: 0,
    }),
  );
  return dataRaw;
}

export function simulationData(_this, data) {
  let dataRaw = futureExpoData(_this);
  let intervalGroup = _this["pickedBucket"];
  let today = timeCompare(_this["today"], intervalGroup, _this["expoDataRaw"]);
  today.setDate(today.getDate() - 1); // subtract one day
  let dat = [];
  let expData = data.filter((d) => d.is_expo);
  let hedgeData = data.filter((d) => !d.is_expo);
  if (expData.length > 0) {
    dat = expData.map(function(d) {
      return {
        balance: +d.balance,
        cash_flow: +d.cash_flow,
        date: d[_this.expoDateField] !== null ? d[_this.expoDateField] : today,
        hedge_value_dn: 0,
        hedge_value_up: 0,
        hedge: 0,
        percentiles_dn: d.percentiles_dn,
        percentiles_up: d.percentiles_up,
        ratio: d.ratio ? +d.ratio : 1,
        unit_change: d.unit_change ? +d.unit_change : 1,
      };
    });
  }

  let dat2 = [];
  if (hedgeData.length > 0) {
    dat2 = hedgeData.map(function(d) {
      return {
        balance: 0,
        cash_flow: 0,
        date:
          d[_this.hedgeDateField] !== null ? d[_this.hedgeDateField] : today,
        hedge_value_dn: d.hedge_value_dn,
        hedge_value_up: d.hedge_value_up,
        hedge: d.hedge,
        percentiles_dn: d.percentiles_dn,
        percentiles_up: d.percentiles_up,
        ratio: d.ratio ? +d.ratio : 1,
        unit_change: d.unit_change ? +d.unit_change : 1,
      };
    });
  }
  dat = dat.concat(dat2.concat(dataRaw));
  return sortByKey(dat, "date");
}

export function getFinalDateExpoHedge(_this) {
  _this.finalDate = max([
    _this.finalDate,
    max(_this.expoDataRaw, function(d) {
      return Number.isFinite(d[_this.expoDateField])
        ? d[_this.expoDateField]
        : -Infinity;
    }),
  ]);
  _this.finalDate = max([
    _this.finalDate,
    max(_this.hedgeDataRaw, function(d) {
      return Number.isFinite(d[_this.hedgeDateField])
        ? d[_this.hedgeDateField]
        : -Infinity;
    }),
  ]);
}

export function sliceData(
  _this,
  intervalField, // day, week, month
  rawDataDField,
  rawDataWField,
  rawDataMField,
  termField, // 1M, 1Y, 2Y...
  resultField,
  xRangeField,
  intervalGroup,
) {
  let data =
    _this[intervalField] === "day"
      ? _this[rawDataDField]
      : _this[intervalField] === "week"
      ? _this[rawDataWField]
      : _this[rawDataMField];
  let selectedDays = lengthByDate(
    _this[termField],
    data.map((d) => d.date),
  );
  _this[resultField] = _this.processData.toSlice(data, selectedDays);
  _this[xRangeField] = _this[resultField].map((d) => d.date);
  _this[xRangeField].push(setMax(data, _this[xRangeField], intervalGroup));
}

/**
 * Retrieves all information related to data in candles for all assets
 * @param {object} _this: context variables
 * @param {string} assetField: string defining the field for asset
 * @param {string} selectedPortfoliosField: string of the name of the variable
 */
export function getAssets(
  _this,
  assetField = "asset",
  selectedPortfoliosField = "selectedPortfolios",
  saveUserPref = false,
  portfoliosStr = "portfolios",
  assetsStr = "assets",
  selectedAssetStr = "selectedAsset",
  analytics_preferencesStr = "analytics_preferences",
  useAllPortfolios = false,
) {
  let assetPK = [],
    i;

  if (useAllPortfolios) {
    for (let i = 0; i < _this[portfoliosStr].length; i++) {
      for (let j = 0; j < _this[portfoliosStr][i][assetField].length; j++) {
        assetPK.push(_this[portfoliosStr][i][assetField][j]);
      }
    }
  } else if (_this[selectedPortfoliosField].length > 0) {
    for (i = 0; i < _this[selectedPortfoliosField].length; i++) {
      for (
        let j = 0;
        j < _this[selectedPortfoliosField][i][assetField].length;
        j++
      ) {
        assetPK.push(_this[selectedPortfoliosField][i][assetField][j]);
      }
    }
  } else {
    if (_this[portfoliosStr].length > 0) {
      assetPK = _this[portfoliosStr][0][assetField];
    }
  }
  assetPK = assetPK.sort();
  assetPK = assetPK.filter(_this.processData.onlyUnique);

  let assets = [];
  let companyParameters = null;
  if ("companyParameters" in _this.$store.state) {
    companyParameters = JSON.parse(
      JSON.stringify(_this.$store.state.companyParameters),
    );
  } else {
    return false;
  }

  if (!_this.isCurrency) {
    assetPK.forEach((pk) => {
      assets.push(
        companyParameters.endProducts.filter(
          (d) => d.pk_id_end_product === pk,
        )[0],
      );
    });
  } else {
    assets = assets.filter(_this.processData.onlyUnique);
    assetPK.forEach((curr) => {
      assets.push(
        _this.currencySpecs.filter((d) => d.currency_name === curr)[0],
      );
    });
  }
  assets = assets.filter(_this.processData.onlyUnique);
  assets = sortByKey(
    assets,
    !_this.isCurrency ? "product_name" : "currency_name",
  );
  if (_this.isCurrency) {
    assets.forEach(function(d) {
      d.product_name = d.currency_name;
    });
  }
  _this[assetsStr] = assets;

  if (assets.length === 0) {
    _this[selectedAssetStr] = !_this.isCurrency
      ? [{ product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." }]
      : [
          {
            currency_name: "EURUSD",
            pk_i_idcurrency: 3,
            fk_idcurrency_right: 0,
            fk_idcurrency_left: 0,
          },
        ];
  } else {
    let selAsset,
      selectedAssets = [];
    if (_this.isInitial) {
      if (!_this.isCurrency) {
        if ("selectedAsset" in _this[analytics_preferencesStr]) {
          selAsset = _this[analytics_preferencesStr].selectedAsset;
        } else {
          selAsset = [assets[0]];
        }
      } else {
        if ("selected_currencies" in _this[analytics_preferencesStr]) {
          selAsset = _this[analytics_preferencesStr].selected_currencies;
        } else {
          selAsset = [assets[0]];
        }
      }
    } else {
      selAsset = _this[selectedAssetStr];
    }

    let idx;
    for (i = 0; i < selAsset.length; i++) {
      if (!_this.isCurrency) {
        idx = assets.findIndex(
          (d) => d.pk_id_end_product === selAsset[i].pk_id_end_product,
        );
      } else {
        idx = assets.findIndex(
          (d) => d.pk_i_idcurrency === selAsset[i].pk_i_idcurrency,
        );
      }
      if (idx > -1) {
        selectedAssets.push(assets[idx]);
      }
    }
    if (selectedAssets.length === 0) {
      selectedAssets = [assets[0]];
    }
    _this[selectedAssetStr] = selectedAssets;
  }
  saveUserPref = true;

  return saveUserPref;
}

/** COMMODITIES **/
export function getPortfolios(_this, asset = "currency", comp) {
  let port = [];
  for (let i = 0; i < comp.length; i++) {
    for (let j = 0; j < comp[i].portfolios.length; j++) {
      port.push({
        portfolio: _this.companies[comp[i].id].portfolios[j],
        companyName: _this.companies[comp[i].id].company_name,
      });
    }
  }
  port = port.filter(_this.processData.onlyUnique);
  port = sortByKey(port, "portfolio_name");
  return assignPortfoliosByAsset(_this, asset, port);
}

export function assignPortfoliosByAsset(_this, asset, port) {
  let opt = [];
  let fieldAsset = asset === "currency" ? "fk_currency" : "fk_idcommodity";

  for (let i = 0; i < port.length; i++) {
    const { portfolio, companyName } = port[i];
    if (portfolio[fieldAsset].length !== 0) {
      opt.push({
        pk: portfolio.pk_idportfolio,
        id: i,
        name: portfolio.portfolio_name,
        underlying: asset === "currency" ? null : portfolio[fieldAsset],
        asset:
          asset === "currency"
            ? portfolio[fieldAsset]
            : portfolio["fk_id_end_product"],
        fkCompany: portfolio.fk_idcompany,
        companyName,
      });
    }
  }
  opt = opt.filter(_this.processData.onlyUnique);
  opt = sortByKey(opt, "name");
  return opt;
}

export function idxOfDate(dates, searchedDate) {
  let idx;
  if (searchedDate >= dates[dates.length - 1]) {
    idx = dates.length - 1;
  } else {
    idx = dates.findIndex((d) => d >= searchedDate);
  }
  return idx;
}

export function rightTable(d) {
  // === tooltip and table
  let total = d.balance + d.cash_flow;
  let positionTOT = total < 0 ? "BUY " : total === 0 ? "" : "SELL ";
  let sgnTOT = Math.sign(total);
  sgnTOT = sgnTOT === 0 ? Math.sign(d.hedge) : sgnTOT;
  let required = d.hedge >= 0 ? d.hedge - d.program : d.program - d.hedge;
  let requiredTxt = required > 0 ? "EXCESS HEDGE" : "REQUIRED HEDGE";
  // === table
  populateTable(
    d,
    positionTOT,
    sgnTOT,
    total,
    numberFormat,
    formatPercent,
    requiredTxt,
    required,
    format(currencyFormat),
    0,
    timeFormat("%Y"),
  );
}

export function getDefaultAnalyticsPreferences(_this) {
  const storeChoices =
    _this.$store.getters["user_preferences/analyticsChoices"];

  if (storeChoices && Object.keys(storeChoices).length > 0) {
    return { choices: storeChoices };
  }

  const defaultChoices = {
    selected_nav: 0,
    color_theme: false,
    selected_company: [],
    selected_portfolio: [],
    selected_currencies: [],
    selected_commodities: [],
    selectedAsset: [],
    mktRef: "spot",
    intervalGroup: "1M",
    selectedAnalysis: "scenarios",
    selectedAnalysisParent: "scenario",
    selectedTable: "strategies",
  };

  _this.$store.dispatch(
    "user_preferences/updateAnalyticsChoices",
    defaultChoices,
  );

  saveUserPreferences(_this);

  return { choices: defaultChoices };
}
